import { useEffect, useMemo, useRef, useState } from 'react';
import api, { formatApiError } from '@/lib/api';
import VoiceActivateModal from '@/components/healthfile/VoiceActivateModal';
import VoiceMicHook from '@/components/healthfile/VoiceMicHook';
import {
  FileText, Sparkles, Lock, ExternalLink, Loader2, Plus, X,
  CheckCircle2, Crown, Shield, MessageSquare,
} from 'lucide-react';

/**
 * HealthFileSection — embedded in the doctor's "Edit visit" composer.
 *
 * Renders the Clinical File body (Reason for Visit, Doctor's Assessment,
 * Medicines, Tests, Red Flags, Follow-up Plan) and a single "Generate
 * Digital Health File" button. After generation the file becomes view-
 * only with a copy-link + "Open patient view" CTA so the doctor can
 * eyeball what the patient will see.
 *
 * Quota UX:
 *   • <3 free files used → primary blue CTA, no friction.
 *   • ≥3 free used + doctor on Turbo → Turbo-pass badge (still free).
 *   • ≥3 free used + non-Turbo → soft amber notice "Patient will pay ₹20
 *     to unlock" + the founder Turbo-upsell copy beneath the button.
 */
export default function HealthFileSection({ target, onDraftChange, existingThreads = [] }) {
  // Names of threads that already exist for this patient — used to detect
  // "returning thread" conditions in the composer (V2+ visits) so the
  // progress-chip + doctor's observation fields only surface on
  // follow-ups. First-time thread creation stays clean.
  const existingThreadNames = useMemo(
    () => new Set((existingThreads || []).map(t => (t.name || '').toLowerCase().trim()).filter(Boolean)),
    [existingThreads]
  );
  const visitId = target?.id;
  const existingFileToken = target?.health_file_token || '';
  const [quota, setQuota] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [err, setErr] = useState('');
  const [created, setCreated] = useState(existingFileToken ? { view_token: existingFileToken, file_id: target?.health_file_id } : null);

  const [form, setForm] = useState({
    reason_for_visit: target?.primary_symptom || target?.reason || '',
    doctor_assessment: target?.notes || '',
    patient_age: target?.patient_age || '',
    medicines: [],
    tests_advised: [],
    red_flags: [],
    dos: [],                        // v9 · visit-level Do's (Simple leaf)
    donts: [],                      // v9 · visit-level Don'ts (Simple leaf)
    follow_up_plan: '',
    consultation_type: 'In-person consultation',
    handwritten_rx_url: '',
    review_date_iso: '',
    // v3 · condition-based DHF
    conditions: [],                 // [{ id, name, icd11_code, snomed_code, symptoms:[], assessment_note, tests_advised:[], red_flags:[], follow_up_plan }]
    past_allergies: [],             // [{ label, severity }]
    short_description: '',          // ≤ 280 chars · shows on WhatsApp preview + top of DHF
  });
  // PpD (Patient-Provided Data) fields — auto-populated from the patient's
  // own onboarding form. When a field is in this set the composer renders
  // it as read-only + shows a "PpD · Patient-Provided" chip. If the patient
  // never provided a value (empty) the field opens for doctor entry and
  // gets labelled DpD on save.
  const ppdFieldsFromPatient = (() => {
    const s = new Set();
    if (target?.patient_age) s.add('patient_age');
    // Reason for visit — if the patient wrote it themselves in the QR
    // check-in / booking form, treat it as PpD too.
    if (target?.primary_symptom || target?.reason) s.add('reason_for_visit');
    return s;
  })();

  // ─── Voice-mode state ────────────────────────────────────────────────
  // Modal fires on first ever tap of 🎙️ Voice mode. Quota is fetched
  // lazily. usedVoiceRef flips true the moment any voice-parse succeeds
  // in this composer session so log-use fires on Generate.
  const [voiceQuota, setVoiceQuota] = useState(null);
  const [voiceModalOpen, setVoiceModalOpen] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const usedVoiceRef = useRef(false);
  const loadVoiceQuota = async () => {
    try {
      const { data } = await api.get('/dashboard/voice/quota');
      setVoiceQuota(data);
      return data;
    } catch (_) { return null; }
  };
  const onVoiceTap = async () => {
    const q = voiceQuota || (await loadVoiceQuota());
    if (!q?.activated) { setVoiceModalOpen(true); return; }
    setVoiceEnabled(v => !v);
  };
  const onVoiceActivated = (payload) => {
    setVoiceQuota(payload?.quota || null);
    setVoiceModalOpen(false);
    setVoiceEnabled(true);
  };
  useEffect(() => {
    // Lazy-load voice quota once so the meter chip renders correctly on
    // first mount without waiting for the doctor to tap the mic.
    loadVoiceQuota();
  }, []);
  // Per-doctor auto-built vocab for medicine row combobox suggestions.
  // Fetched on mount; refreshed lazily as the doctor types new values.
  const [vocab, setVocab] = useState({ medicine_name: [], strength: [], dose: [], duration: [] });
  const [rxUploading, setRxUploading] = useState(false);
  const [rxErr, setRxErr] = useState('');
  // Ephemeral toast messages that show inside the composer for a few
  // seconds and then fade out. Keyed by id so multiple can stack.
  const [toasts, setToasts] = useState([]);
  const pushToast = (msg, tone = 'sky') => {
    const id = Date.now() + Math.random();
    setToasts(t => [...t, { id, msg, tone }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 3200);
  };
  const [pullingLast, setPullingLast] = useState(false);
  const [lastDhfMeta, setLastDhfMeta] = useState(null); // { file_id, doctor_name, visit_date, same_doctor }

  useEffect(() => {
    if (!visitId) return;
    let live = true;
    (async () => {
      try {
        const { data } = await api.get(`/dashboard/health-file/quota`, { params: { visit_id: visitId } });
        if (!live) return;
        setQuota(data);
        // Hydrate the editable clinical body from a previously saved draft so
        // the doctor's medicines/tests reload and stay editable.
        const d = data.dhf_draft;
        if (d && typeof d === 'object') {
          setForm(f => ({
            ...f,
            reason_for_visit: d.reason_for_visit || f.reason_for_visit,
            doctor_assessment: d.doctor_assessment || f.doctor_assessment,
            patient_age: d.patient_age || f.patient_age,
            consultation_type: d.consultation_type || f.consultation_type,
            medicines: Array.isArray(d.medicines) && d.medicines.length ? d.medicines : f.medicines,
            tests_advised: Array.isArray(d.tests_advised) ? d.tests_advised : f.tests_advised,
            red_flags: Array.isArray(d.red_flags) ? d.red_flags : f.red_flags,
            follow_up_plan: d.follow_up_plan || f.follow_up_plan,
            handwritten_rx_url: d.handwritten_rx_url || f.handwritten_rx_url,
            review_date_iso: d.review_date_iso || f.review_date_iso,
            conditions: Array.isArray(d.conditions) ? d.conditions : f.conditions,
            past_allergies: Array.isArray(d.past_allergies) ? d.past_allergies : f.past_allergies,
            short_description: d.short_description || f.short_description,
          }));
        }
      } catch (e) { /* non-fatal */ }
    })();
    return () => { live = false; };
  }, [visitId]);

  // Fetch this doctor's personal medicine vocab so every row's Name /
  // Strength / Dose / Duration fields render a native <datalist>
  // dropdown of prior values — reduces typing on repeat prescriptions.
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const { data } = await api.get('/dashboard/prescription-vocab');
        if (live && data) setVocab({
          medicine_name: data.medicine_name || [],
          strength: data.strength || [],
          dose: data.dose || [],
          duration: data.duration || [],
        });
      } catch (_) { /* silent — dropdowns just stay empty */ }
    })();
    return () => { live = false; };
  }, []);

  // Surface the live clinical draft to the parent composer so the
  // "Draft Save · Clinical Data" button can persist it on the visit.
  useEffect(() => {
    if (onDraftChange) onDraftChange(form);
  }, [form, onDraftChange]);

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // ─── Auto-summariser for the patient-facing Short Description ────────────
  // Rule-based, no LLM call — runs on every field change (debounced 400ms).
  // Stops the moment the doctor edits the Short Description manually so we
  // never overwrite their thought. A "Resume auto-fill" link brings it back.
  const shortDescManualRef = useRef(false);
  const shortDescTimerRef = useRef(null);
  const composeShortDesc = () => {
    const parts = [];
    const firstLine = (form.doctor_assessment || '').split('\n').map(s => s.trim()).filter(Boolean)[0];
    if (firstLine) parts.push(firstLine.replace(/\.$/, ''));
    const threadNames = (form.conditions || []).map(c => (c.name || '').trim()).filter(Boolean);
    if (threadNames.length) parts.push(`Threads: ${threadNames.slice(0, 3).join(' + ')}`);
    const topMeds = (form.medicines || [])
      .filter(m => (m.name || '').trim())
      .slice(0, 2)
      .map(m => [(m.name || '').trim(), (m.strength || '').trim()].filter(Boolean).join(' '))
      .filter(Boolean);
    if (topMeds.length) parts.push(`Rx: ${topMeds.join(', ')}${(form.medicines || []).length > 2 ? ' + more' : ''}`);
    const followRaw = (form.follow_up_plan || '').trim() || (form.conditions || []).map(c => (c.follow_up_plan || '').trim()).filter(Boolean)[0] || '';
    if (followRaw) parts.push(followRaw);
    let out = parts.join('. ');
    if (out.length > 240) {
      out = out.slice(0, 237).replace(/\s+\S*$/, '') + '…';
    }
    return out;
  };
  useEffect(() => {
    if (shortDescManualRef.current) return;               // paused — doctor is driving
    if (shortDescTimerRef.current) clearTimeout(shortDescTimerRef.current);
    shortDescTimerRef.current = setTimeout(() => {
      const next = composeShortDesc();
      setForm(f => (next === f.short_description ? f : { ...f, short_description: next }));
    }, 400);
    return () => { if (shortDescTimerRef.current) clearTimeout(shortDescTimerRef.current); };
  }, [form.doctor_assessment, form.conditions, form.medicines, form.follow_up_plan]);
  const onShortDescChange = (v) => {
    shortDescManualRef.current = true;
    setField('short_description', v);
  };
  const resumeAutoShortDesc = () => {
    shortDescManualRef.current = false;
    setField('short_description', composeShortDesc());
  };

  const addMed = () => {
    // Auto-tag new med row to the most recently added condition (if any)
    // so the doctor's "add condition → add its medicines" flow feels natural.
    const lastCond = (form.conditions || []).slice(-1)[0];
    const owner = lastCond?.id || '';
    setField('medicines', [...form.medicines, { name: '', strength: '', dose: '', frequency: '', duration: '', notes: '', meal_timings: [], form_type: 'tab', molecule: '', drug_class: '', schedule: '', quantity: '', condition_id: owner, cross_condition_ids: [], milestone_ids: [] }]);
  };
  const updMed = (i, k, v) => setField('medicines', form.medicines.map((m, idx) => idx === i ? { ...m, [k]: v } : m));
  // Batch update — merge many fields at once. Fixes the state-batching bug
  // where multiple sequential updMed calls (from async onBlur handlers) each
  // overwrote the previous because they all captured the same stale array.
  const updMedMany = (i, patch) => setField('medicines', form.medicines.map((m, idx) => idx === i ? { ...m, ...patch } : m));
  const rmMed = (i) => setField('medicines', form.medicines.filter((_, idx) => idx !== i));

  // Push a new value onto the doctor's private vocab so it becomes a
  // suggestion for future rows. Fire-and-forget — never blocks the UI.
  const learnVocab = async (field, value) => {
    const v = (value || '').trim();
    if (!v) return;
    if ((vocab[field] || []).includes(v)) return; // already known
    setVocab(prev => ({ ...prev, [field]: [...(prev[field] || []), v] }));
    try { await api.post('/dashboard/prescription-vocab', { field, value: v }); } catch (_) { /* silent */ }
  };

  // Handwritten prescription upload (Turbo-only). Compress client-side
  // with a canvas resize so we don't waste egress on 5 MB camera dumps.
  const compressImage = (file, maxWidth = 1600, quality = 0.82) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const scale = img.width > maxWidth ? maxWidth / img.width : 1;
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);
        canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error('encode failed')), 'image/jpeg', quality);
      };
      img.onerror = () => reject(new Error('bad image'));
      img.src = reader.result;
    };
    reader.onerror = () => reject(new Error('read failed'));
    reader.readAsDataURL(file);
  });

  const onRxPicked = async (e) => {
    setRxErr('');
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type?.startsWith('image/')) { setRxErr('Please pick an image.'); return; }
    setRxUploading(true);
    try {
      const blob = await compressImage(file);
      const fd = new FormData();
      fd.append('file', blob, `rx-${Date.now()}.jpg`);
      const { data } = await api.post('/dashboard/health-file/rx-photo-upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setField('handwritten_rx_url', data.url);
    } catch (er) {
      setRxErr(formatApiError(er.response?.data?.detail) || 'Upload failed. Please retry.');
    } finally { setRxUploading(false); }
  };

  // "Pull last DHF" — fetches the patient's most recent DHF (any doctor)
  // and hydrates the composer for quick incremental edits. Shows a
  // short auto-fading toast so the doctor knows what happened.
  const pullLastDhf = async () => {
    if (!visitId) return;
    setPullingLast(true);
    try {
      const { data } = await api.get('/dashboard/health-file/last-for-patient', { params: { visit_id: visitId } });
      if (!data.found) {
        pushToast('No prior DHF found for this patient.', 'slate');
        return;
      }
      const c = data.clinical || {};
      setForm(f => ({
        ...f,
        reason_for_visit: c.reason_for_visit || f.reason_for_visit,
        doctor_assessment: c.doctor_assessment || f.doctor_assessment,
        medicines: Array.isArray(c.medicines) && c.medicines.length ? c.medicines : f.medicines,
        tests_advised: Array.isArray(c.tests_advised) && c.tests_advised.length ? c.tests_advised : f.tests_advised,
        red_flags: Array.isArray(c.red_flags) && c.red_flags.length ? c.red_flags : f.red_flags,
        follow_up_plan: c.follow_up_plan || f.follow_up_plan,
      }));
      setLastDhfMeta({
        file_id: data.file_id,
        doctor_name: data.doctor_name,
        visit_date: data.visit_date,
        same_doctor: data.same_doctor,
      });
      pushToast('Pulled last DHF · edit deltas to save time · consistency preserved.', 'emerald');
    } catch (_) {
      pushToast('Could not pull last DHF. Please retry.', 'rose');
    } finally { setPullingLast(false); }
  };

  // Auto-append `Review Date: DD MMM YYYY` when the doctor's follow-up
  // sentence says `review after N days / weeks / months`. Doctor can
  // still edit the resulting string — we only trigger once per pattern.
  const REVIEW_RX = /review\s+(?:after|in)\s+(\d{1,3})\s*(day|days|week|weeks|month|months)/i;
  const onFollowUpChange = (raw) => {
    // v9 · fix space-bar bug: no longer append "· Review Date: ..." into the
    // text (that was fighting the user's typing on every keystroke). The
    // review date is still parsed and shown as a chip below the input via
    // `form.review_date_iso`.
    const text = raw;
    const m = REVIEW_RX.exec(text);
    let reviewIso = '';
    if (m) {
      const n = parseInt(m[1], 10);
      const unit = m[2].toLowerCase();
      const days = unit.startsWith('week') ? n * 7 : unit.startsWith('month') ? n * 30 : n;
      const base = new Date(target?.visit_date || target?.created_at || Date.now());
      const rev = new Date(base.getTime() + days * 86400000);
      reviewIso = rev.toISOString().slice(0, 10);
    }
    setForm(f => ({ ...f, follow_up_plan: text, review_date_iso: reviewIso }));
  };

  const onGenerate = async () => {
    setErr(''); setGenerating(true);
    try {
      const tests_advised = (form.tests_advised || []).map(s => s.trim()).filter(Boolean);
      const red_flags = (form.red_flags || []).map(s => s.trim()).filter(Boolean);
      // Compute PpD fields at Generate time — anything the patient
      // originally provided (and the doctor did NOT overwrite to empty)
      // gets stamped as Patient-Provided-Data on the sealed file.
      const ppd_fields = Array.from(ppdFieldsFromPatient).filter(k => (form[k] || '').toString().trim());
      const { data } = await api.post('/dashboard/health-file/create', {
        visit_id: visitId,
        reason_for_visit: form.reason_for_visit,
        doctor_assessment: form.doctor_assessment,
        patient_age: form.patient_age,
        medicines: form.medicines.filter(m => (m.name || '').trim()),
        tests_advised,
        red_flags,
        follow_up_plan: form.follow_up_plan,
        // v9 · Visit-level Do's / Don'ts (Simple condition flow). Strip empty lines.
        dos: (form.dos || []).map(s => (s || '').trim()).filter(Boolean).slice(0, 20),
        donts: (form.donts || []).map(s => (s || '').trim()).filter(Boolean).slice(0, 20),
        consultation_type: form.consultation_type,
        handwritten_rx_url: form.handwritten_rx_url || '',
        review_date_iso: form.review_date_iso || '',
        // v3 · condition-based DHF
        conditions: (form.conditions || []).filter(c => (c.name || '').trim()),
        past_allergies: (form.past_allergies || []).filter(a => (a.label || '').trim()),
        short_description: (form.short_description || '').slice(0, 280),
        ppd_fields,
      });
      setCreated(data);
      // Refresh quota counter
      if (quota) setQuota({ ...quota, free_used: (quota.free_used || 0) + 1, free_remaining: Math.max(0, (quota.free_remaining || 0) - 1) });

      // If any voice-parsing happened during this composer session, meter it.
      // Fire-and-forget: never blocks the Generate success flow.
      if (usedVoiceRef.current && data?.file_id) {
        try {
          const { data: logRes } = await api.post('/dashboard/voice/log-use', {
            file_id: data.file_id,
            fields_used: ['medicine', 'assessment', 'followup'],
          });
          if (logRes?.quota) setVoiceQuota(logRes.quota);
        } catch (_) { /* silent */ }
        usedVoiceRef.current = false;
      }
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || 'Could not generate health file');
    } finally { setGenerating(false); }
  };

  // ─── Already created — show success card ────────────────────────────────
  if (created?.view_token) {
    const url = `${typeof window !== 'undefined' ? window.location.origin : ''}/h/${created.view_token}`;
    const fmtD = (iso) => { try { return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }); } catch { return iso || ''; } };
    return (
      <div className="rounded-2xl border border-emerald-200 bg-emerald-50/50 p-4" data-testid="dhf-created-card">
        <div className="flex items-start gap-2.5">
          <div className="w-10 h-10 rounded-xl bg-white border border-emerald-200 flex items-center justify-center shrink-0">
            <CheckCircle2 size={18} className="text-emerald-600" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-700">Digital Health File generated</p>
            <p className="text-[11px] text-slate-600 mt-1">Sealed and shared with the patient.{' '}
              {created.notify?.channel === 'whatsapp' && <>WhatsApp link sent (their 1st DocSite file).</>}
              {created.notify?.channel === 'push' && <>In-app push notification fired.</>}
            </p>

            {/* Visit anchor preview — mirrors what the patient sees */}
            <div className="mt-3 rounded-xl border border-slate-200 bg-white p-3" data-testid="dhf-created-anchor">
              <p className="text-[9px] font-bold uppercase tracking-[0.18em] text-slate-400">What your patient sees · Your visit with</p>
              <p className="text-sm font-bold text-slate-900 mt-0.5">{quota?.doctor_name || target?.doctor_name || 'You'}</p>
              {quota?.doctor_specialty && <p className="text-[11px] text-slate-500">{quota.doctor_specialty}</p>}
              <p className="text-[12px] text-slate-700 mt-1 flex items-center flex-wrap gap-1">
                <span>{fmtD(quota?.visit_date || target?.visit_date || target?.created_at)}</span>
                {(quota?.clinic_location) && <><span className="text-slate-400">·</span><span>{quota.clinic_location}</span></>}
              </p>
              <div className="mt-2 pt-2 border-t border-slate-100 flex items-center justify-between gap-2">
                <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">File ID</span>
                <span className="font-mono text-[11px] text-slate-600">{created.file_id}</span>
              </div>
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <a href={url} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800" data-testid="dhf-open">
                Open patient view <ExternalLink size={11} />
              </a>
              <button onClick={() => { navigator.clipboard?.writeText(url); }}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-medium text-slate-700 hover:bg-white"
                data-testid="dhf-copy">
                Copy link
              </button>
              {created.notify?.wa_link && (
                <a href={created.notify.wa_link} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-emerald-300 text-xs font-medium text-emerald-700 hover:bg-emerald-50"
                  data-testid="dhf-wa-resend">
                  <MessageSquare size={11} /> Send via WhatsApp
                </a>
              )}
            </div>
            <p className="text-[10px] text-slate-400 mt-2 italic flex items-center gap-1"><Lock size={9} /> Locked after creation — non-editable.</p>
          </div>
        </div>
      </div>
    );
  }

  // ─── Pre-creation form ──────────────────────────────────────────────────
  const used = quota?.free_used ?? 0;
  const limit = quota?.free_limit ?? 3;
  const remaining = quota?.free_remaining ?? Math.max(0, limit - used);
  const isTurboPass = !!quota?.doctor_turbo_pass;
  const overFree = used >= limit;
  const willCostPatient = overFree && !isTurboPass;

  // Apply a Gemini-parsed voice JSON to the last (or newly-added) medicine
  // row. Called from the medicine-row voice mic. Marks used_voice=true so
  // /log-use fires when the doctor Generates the DHF.
  const applyVoicedMedicine = (parsed) => {
    if (!parsed || typeof parsed !== 'object') return;
    usedVoiceRef.current = true;
    setForm(f => {
      const meds = Array.isArray(f.medicines) ? [...f.medicines] : [];
      const lastIdx = meds.length - 1;
      const isBlank = lastIdx < 0 || (!(meds[lastIdx]?.name || '').trim());
      const lastCond = (f.conditions || []).slice(-1)[0];
      const nextRow = {
        name: parsed.name || '',
        strength: parsed.strength || '',
        dose: parsed.dose || '',
        frequency: parsed.frequency || '',
        duration: parsed.duration || '',
        notes: parsed.notes || '',
        meal_timings: Array.isArray(parsed.meal_timings) ? parsed.meal_timings.slice(0, 6) : [],
        form_type: parsed.form_type || 'tab',
        molecule: parsed.molecule || '',
        drug_class: parsed.drug_class || '',
        schedule: parsed.schedule || '',
        quantity: parsed.quantity || '',
        condition_id: lastCond?.id || '',
        cross_condition_ids: [],
        milestone_ids: [],
      };
      if (isBlank) {
        if (lastIdx < 0) meds.push(nextRow);
        else meds[lastIdx] = { ...meds[lastIdx], ...nextRow };
      } else {
        meds.push(nextRow);
      }
      return { ...f, medicines: meds };
    });
  };
  const applyVoicedFollowup = (p) => {
    if (!p?.follow_up_plan) return;
    usedVoiceRef.current = true;
    onFollowUpChange(p.follow_up_plan);
  };

  return (
    <div className="rounded-2xl border border-sky-200 bg-gradient-to-br from-sky-50/40 to-white p-4" data-testid="dhf-section">
      <VoiceActivateModal
        open={voiceModalOpen}
        onClose={() => setVoiceModalOpen(false)}
        onActivated={onVoiceActivated}
        quota={voiceQuota}
      />
      {/* Header */}
      <div className="flex items-start gap-2.5 mb-3">
        <div className="w-10 h-10 rounded-xl bg-white border border-sky-200 flex items-center justify-center shrink-0">
          <FileText size={18} className="text-sky-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Digital Health File</p>
            <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 uppercase tracking-wider">Patient-Owned</span>
            {isTurboPass && (
              <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-100 text-amber-800 border border-amber-300 uppercase tracking-wider flex items-center gap-1"><Crown size={9} /> Turbo Pass · Free</span>
            )}
          </div>
          <p className="text-[11px] text-slate-600 mt-0.5">Fill the clinical body below, then generate a sealed file the patient receives instantly.</p>
          {/* Quota context chips */}
          {quota && (
            <div className="flex flex-wrap items-center gap-1.5 mt-2" data-testid="dhf-quota-chips">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-[10px] font-bold text-emerald-700">
                {quota.patient_name ? `${quota.patient_name} · ` : ''}used {quota.free_used ?? 0} of {quota.free_limit ?? 3} free files (DocSite-wide)
              </span>
              {(quota.free_used ?? 0) >= (quota.free_limit ?? 3) && !quota.doctor_turbo_pass && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 border border-amber-300 text-[10px] font-bold text-amber-800">
                  <Crown size={9} /> Past 3 free — patient sees ₹{quota.paid_price_inr || 20} paywall + Turbo nudge
                </span>
              )}
            </div>
          )}
        </div>
        {quota && (
          <div className="text-right shrink-0">
            <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400">Free quota</p>
            <p className="text-sm font-bold text-slate-900 tabular-nums">{remaining}/{limit}</p>
          </div>
        )}
      </div>

      {/* Quota notice */}
      {willCostPatient && (
        <div className="mb-3 rounded-xl border-2 border-amber-300 bg-amber-50 p-3" data-testid="dhf-paid-notice">
          <p className="text-[11px] font-bold text-amber-800 uppercase tracking-wider flex items-center gap-1"><Sparkles size={12} /> Patient is past 3 free files</p>
          <p className="text-xs text-amber-800 mt-1 leading-relaxed">
            The patient will receive a WhatsApp link to unlock this file for <b>₹{quota?.paid_price_inr || 20}</b> per file (1-year cloud storage, unlimited views).
          </p>
          {quota?.patient_turbo_upsell_message && (
            <p className="text-[11px] text-amber-800 mt-2 italic leading-relaxed">
              They will also see this nudge on their viewer: <b>“{quota.patient_turbo_upsell_message}”</b>
            </p>
          )}
        </div>
      )}

      {/* Ephemeral toast stack — inline, auto-fades in 3.2s */}
      {toasts.length > 0 && (
        <div className="mb-3 space-y-1" data-testid="dhf-toast-stack">
          {toasts.map(t => (
            <div key={t.id} className={
              `text-[11px] font-semibold rounded-lg px-2.5 py-1.5 border animate-in fade-in slide-in-from-top-1 duration-200 ${
                t.tone === 'emerald' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' :
                t.tone === 'rose' ? 'bg-rose-50 border-rose-200 text-rose-800' :
                'bg-slate-50 border-slate-200 text-slate-700'}`
            } data-testid={`dhf-toast-${t.tone}`}>
              {t.msg}
            </div>
          ))}
        </div>
      )}

      {/* Full-screen overlay while sealing the DHF (also auto-issues the Rx).
          Doctor gets clear feedback for the 10-25s wait, regardless of
          scroll position — the inline button spinner is often below the fold. */}
      {generating && (
        <div className="fixed inset-0 bg-slate-900/55 z-[9999] flex items-center justify-center"
             data-testid="dhf-generating-overlay">
          <div className="bg-white rounded-2xl shadow-2xl px-8 py-6 text-center max-w-sm">
            <Loader2 size={40} className="mx-auto mb-3 text-sky-600 animate-spin" />
            <div className="text-slate-900 font-bold text-base">Sealing Digital Health File…</div>
            <div className="text-slate-500 text-xs mt-1">
              Encrypting · issuing prescription · notifying patient
            </div>
            <div className="text-slate-400 text-[10px] mt-3 italic">This can take 10-20 seconds</div>
          </div>
        </div>
      )}

      {/* One-tap "Pull last DHF" — patient's most recent DHF across all
          doctors. Auto-populates the composer with medicines / tests /
          red flags / follow-up so the doctor only needs to add deltas. */}
      {visitId && !created && (
        <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="dhf-pull-last-row">
          <button
            onClick={pullLastDhf}
            disabled={pullingLast}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-sky-50 border border-sky-200 text-sky-800 text-xs font-semibold hover:bg-sky-100 disabled:opacity-50"
            data-testid="dhf-pull-last-btn"
          >
            {pullingLast ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />}
            {pullingLast ? 'Pulling…' : 'Pull last DHF for this patient'}
          </button>
          <button
            type="button"
            onClick={onVoiceTap}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold ${
              voiceEnabled ? 'bg-sky-600 text-white border border-sky-600' : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'
            }`}
            data-testid="dhf-voice-btn"
            title={voiceEnabled ? 'Voice mode ON · tap to turn off' : 'Activate voice-enabled prescriptions'}
          >
            🎙️ {voiceEnabled ? 'Voice ON' : 'Voice mode'}
            {voiceQuota?.activated && voiceQuota.free_remaining > 0 && (
              <span className="ml-1 text-[9px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800 border border-emerald-200 rounded-full px-1.5 py-0.5" data-testid="dhf-voice-quota-chip">
                {voiceQuota.free_remaining} FREE
              </span>
            )}
            {voiceQuota?.activated && voiceQuota.free_remaining === 0 && voiceQuota.turbo_month_remaining > 0 && (
              <span className="ml-1 text-[9px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-200 rounded-full px-1.5 py-0.5" data-testid="dhf-voice-quota-chip">
                👑 {voiceQuota.turbo_month_remaining}/mo
              </span>
            )}
            {voiceQuota?.activated && voiceQuota.free_remaining === 0 && voiceQuota.turbo_month_remaining === 0 && (
              <span className="ml-1 text-[9px] font-bold uppercase tracking-wider bg-slate-100 text-slate-700 border border-slate-200 rounded-full px-1.5 py-0.5" data-testid="dhf-voice-quota-chip">
                ₹5/file
              </span>
            )}
            {!voiceQuota?.activated && (
              <span className="ml-1 text-[9px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-200 rounded-full px-1.5 py-0.5">NEW</span>
            )}
          </button>
          {lastDhfMeta && (
            <span className="text-[10px] text-slate-500 flex items-center gap-1" data-testid="dhf-pulled-meta">
              Loaded {lastDhfMeta.file_id} · from your prior visit{lastDhfMeta.visit_date ? ` · ${String(lastDhfMeta.visit_date).slice(0, 10)}` : ''}
            </span>
          )}
        </div>
      )}

      {/* NEW (Aug 2026) · Baseline Conditions + Past Surgeries — sealed
          at first source of truth. On V1 the doctor captures; on V2+ the
          rows render read-only. Rendered above Past Allergies since
          together they form the patient's medical baseline. */}
      <PatientBaselineBlock target={target} />

      {/* NEW · Past Allergies (visit-global · carries forward) */}
      <div className="mb-3 rounded-2xl border border-rose-200 bg-rose-50/40 p-3" data-testid="dhf-allergies-block">
        <div className="flex items-center gap-2 mb-2">
          <Shield size={12} className="text-rose-600" />
          <p className="text-[10px] font-bold uppercase tracking-wider text-rose-800">Past Allergies</p>
          <span className="ml-auto text-[9px] font-bold uppercase tracking-wider bg-sky-100 text-sky-800 border border-sky-200 px-1.5 py-0.5 rounded-full flex items-center gap-1">
            <Lock size={8} /> DpD
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-1.5" data-testid="dhf-allergy-chips">
          {(form.past_allergies || []).map((a, i) => (
            <span key={i} className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-full bg-white border border-rose-200 text-[11px] font-semibold text-rose-800" data-testid={`dhf-allergy-chip-${i}`}>
              {a.label}
              <span className={`text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded ${
                a.severity === 'severe' ? 'bg-rose-600 text-white' :
                a.severity === 'moderate' ? 'bg-amber-500 text-white' :
                'bg-slate-400 text-white'
              }`}>{a.severity || 'mild'}</span>
              <button onClick={() => setField('past_allergies', form.past_allergies.filter((_, idx) => idx !== i))}
                className="text-rose-500 hover:text-rose-700" data-testid={`dhf-allergy-rm-${i}`}>×</button>
            </span>
          ))}
          <AllergyAdder onAdd={(item) => setField('past_allergies', [...(form.past_allergies || []), item])} />
        </div>
        {(form.past_allergies || []).length > 0 && (
          <p className="text-[10px] text-rose-700 mt-2 flex items-start gap-1" data-testid="dhf-allergy-warn">
            <span>⚠️</span>
            <span><b>Safety cross-check on:</b> any medicine in any condition that matches an allergy above will highlight in rose before Generate.</span>
          </p>
        )}
      </div>

      {/* Clinical fields */}
      <div className="grid md:grid-cols-2 gap-3">
        <DhfField label="Reason for visit" full ppd={ppdFieldsFromPatient.has('reason_for_visit') && String(form.reason_for_visit || '').trim()}>
          <textarea rows={2} value={form.reason_for_visit} onChange={e => setField('reason_for_visit', e.target.value)}
            readOnly={ppdFieldsFromPatient.has('reason_for_visit') && String(form.reason_for_visit || '').trim() !== ''}
            className={`w-full rounded-lg border border-slate-200 px-2.5 py-2 text-sm resize-y ${ppdFieldsFromPatient.has('reason_for_visit') && String(form.reason_for_visit || '').trim() ? 'bg-slate-50 cursor-not-allowed' : ''}`} data-testid="dhf-reason" />
        </DhfField>
        <DhfField label="Patient age" ppd={ppdFieldsFromPatient.has('patient_age') && String(form.patient_age || '').trim()}>
          <input value={form.patient_age} onChange={e => setField('patient_age', e.target.value)}
            readOnly={ppdFieldsFromPatient.has('patient_age') && String(form.patient_age || '').trim() !== ''}
            placeholder="e.g. 42"
            className={`w-full rounded-lg border border-slate-200 px-2.5 py-2 text-sm ${ppdFieldsFromPatient.has('patient_age') && String(form.patient_age || '').trim() ? 'bg-slate-50 cursor-not-allowed' : ''}`} data-testid="dhf-age" />
        </DhfField>
        <DhfField label="Consultation type" dpd>
          <input value={form.consultation_type} onChange={e => setField('consultation_type', e.target.value)}
            placeholder="In-person consultation"
            className="w-full rounded-lg border border-slate-200 px-2.5 py-2 text-sm" data-testid="dhf-consult-type" />
        </DhfField>
      </div>

      {/* ═══════════ SIMPLE-or-COMPLEX super-block · one clinical choice ═══════════ */}
      {/* v9.2 · Doctor picks EITHER a free-text simple assessment OR a threaded    */}
      {/* longitudinal case. The outer border makes it visually one decision block. */}
      <div className="mt-3 rounded-2xl border-2 border-slate-300 bg-slate-50/30 p-3" data-testid="dhf-condition-superblock">
        {/* Simple Medical Condition — sky-tinted card */}
        <div className={`rounded-2xl border border-sky-200 bg-gradient-to-b from-sky-50/60 to-white p-3 ${(form.conditions || []).length > 0 ? 'opacity-60 pointer-events-none' : ''}`} data-testid="dhf-simple-block">
          <DhfField label="Simple Medical Condition : Doctor's Assessment (first line = DIAGNOSIS)" full dpd>
            <textarea rows={3} value={form.doctor_assessment} onChange={e => setField('doctor_assessment', e.target.value)}
              disabled={(form.conditions || []).length > 0}
              placeholder={'Viral upper respiratory infection\nNo signs of bacterial infection\nCondition is mild and self-limiting'}
              className={`w-full rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-sm resize-y ${(form.conditions || []).length > 0 ? 'bg-slate-50 opacity-60 cursor-not-allowed' : ''}`} data-testid="dhf-assessment" />
            {(form.conditions || []).length > 0 && (
              <p className="text-[10px] text-slate-500 mt-1 italic">Disabled — you are using the Complex Medical Conditions (Health Threads) flow below.</p>
            )}
            {voiceEnabled && voiceQuota?.activated && (form.conditions || []).length === 0 && (
              <div className="mt-1.5">
                <VoiceMicHook target="assessment" label="Dictate assessment" value={form.doctor_assessment} onLiveChange={(t) => setField('doctor_assessment', t)} onUsed={() => { usedVoiceRef.current = true; }} />
              </div>
            )}
          </DhfField>
        </div>

        {/* v9.2 · OR divider — larger, bolder, centered chip */}
        <div className="flex items-center gap-3 my-4" data-testid="dhf-or-divider">
          <div className="flex-1 h-px bg-slate-300" />
          <span className="text-[15px] font-black uppercase tracking-[0.14em] text-slate-700 bg-white border border-slate-300 rounded-full px-4 py-1 shadow-sm">OR</span>
          <div className="flex-1 h-px bg-slate-300" />
        </div>

        {/* ═══════════ HEALTH THREADS · longitudinal cross-visit tracking ═══════════ */}
        {/* Optional block · sits between Assessment and Medicines so a doctor can group */}
        {/* medicines under their parent thread. Skip it entirely to keep the flat flow. */}
        <div className={`rounded-2xl border border-emerald-200 bg-gradient-to-b from-emerald-50/50 to-white p-3 ${String(form.doctor_assessment || '').trim() ? 'opacity-60 pointer-events-none' : ''}`} data-testid="dhf-conditions-block">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-700">Complex Medical Conditions : Health Threads · Longitudinal Case Files</p>
          <span className="text-[9px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800 border border-emerald-200 px-1.5 py-0.5 rounded-full">NEW · OPTIONAL</span>
          <span className="ml-auto text-[9px] font-bold uppercase tracking-wider bg-sky-100 text-sky-800 border border-sky-200 px-1.5 py-0.5 rounded-full flex items-center gap-1"><Lock size={8} /> DpD</span>
        </div>
        {String(form.doctor_assessment || '').trim() && (
          <p className="text-[10px] text-slate-500 italic mb-2">Disabled — you are using the Simple Medical Condition (Doctor&rsquo;s Assessment) flow above.</p>
        )}
        <p className="text-[10px] text-slate-500 mb-2 leading-relaxed">
          Open a thread to have DocSite track this issue <b>across every future visit</b> until you mark it cured or concluded.
          Each thread owns its own symptoms · tests · red-flags · follow-up.
          Medicines below can be tagged to a thread. Skip this section to stay with the flat, one-visit flow.
        </p>
        <div className="space-y-2" data-testid="dhf-conditions-list">
          {(form.conditions || []).map((c, i) => {
            const isReturning = existingThreadNames.has((c.name || '').toLowerCase().trim());
            const linkedMedCount = (form.medicines || []).filter(m => m.condition_id === c.id).length;
            return (
            <ConditionBlockRow key={c.id || i}
              cond={c}
              idx={i}
              color={['sky','amber','violet'][i % 3]}
              isReturning={isReturning}
              linkedMedCount={linkedMedCount}
              onUpdate={(patch) => setField('conditions', form.conditions.map((x, idx) => idx === i ? { ...x, ...patch } : x))}
              onRemove={() => setField('conditions', form.conditions.filter((_, idx) => idx !== i))}
            />
          );})}
        </div>
        <button
          type="button"
          onClick={() => setField('conditions', [...(form.conditions || []), { id: `c-${Date.now()}-${Math.floor(Math.random()*999)}`, name: '', icd11_code: '', snomed_code: '', symptoms: [], assessment_note: '', tests_advised: [], red_flags: [], follow_up_plan: '', status: 'open', milestones: [], visit_outcome: '', doctor_inference: '', opd_procedure_name: '', opd_procedure_notes: '', dos: [], donts: [] }])}
          className="mt-2 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-white border border-emerald-300 text-emerald-800 text-xs font-semibold hover:bg-emerald-50"
          data-testid="dhf-add-condition">
          <Plus size={11} /> Open new health thread
        </button>
      </div>
      </div>
      {/* ─── /SIMPLE-or-COMPLEX super-block ─── */}

      {/* ═══════════ Medicines · Tests · Red-flags · Follow-up · Turbo Rx ═══════════ */}
      <div className="grid md:grid-cols-2 gap-3 mt-3">
        <DhfField label="Medicines prescribed" full dpd>
          <div className="space-y-3">
            {form.medicines.map((m, i) => (
              <MedicineRow
                key={i}
                m={m}
                idx={i}
                vocab={vocab}
                conditions={form.conditions}
                onUpdate={updMed}
                onUpdateMany={updMedMany}
                onRemove={rmMed}
                onLearn={learnVocab}
              />
            ))}
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <button onClick={addMed} className="inline-flex items-center gap-1 text-xs text-sky-600 hover:text-sky-700 font-semibold" data-testid="dhf-add-med">
                <Plus size={11} /> Add medicine
              </button>
              {voiceEnabled && voiceQuota?.activated && (
                <VoiceMicHook target="medicine" label="Dictate medicine" onParsed={applyVoicedMedicine} onUsed={() => { usedVoiceRef.current = true; }} />
              )}
            </div>
          </div>
          {/* Shared datalists — one <datalist> feeds all rows via list= */}
          <datalist id="dhf-vocab-medicine">{(vocab.medicine_name || []).map(v => <option key={v} value={v} />)}</datalist>
          <datalist id="dhf-vocab-strength">{(vocab.strength || []).map(v => <option key={v} value={v} />)}</datalist>
          <datalist id="dhf-vocab-dose">{(vocab.dose || []).map(v => <option key={v} value={v} />)}</datalist>
          <datalist id="dhf-vocab-duration">{(vocab.duration || []).map(v => <option key={v} value={v} />)}</datalist>
        </DhfField>

        <DhfField label="Tests advised (one per line)" dpd>
          <textarea rows={2} value={(form.tests_advised || []).join('\n')}
            onChange={e => setField('tests_advised', e.target.value.split('\n'))}
            placeholder={'CBC\nLipid profile'}
            className="w-full rounded-lg border border-slate-200 px-2.5 py-2 text-sm resize-y" data-testid="dhf-tests" />
        </DhfField>
        <DhfField label="Red flags · when to seek help (one per line)" dpd>
          <textarea rows={2} value={(form.red_flags || []).join('\n')}
            onChange={e => setField('red_flags', e.target.value.split('\n'))}
            placeholder={'Fever > 102°F\nWorsening pain'}
            className="w-full rounded-lg border border-slate-200 px-2.5 py-2 text-sm resize-y" data-testid="dhf-redflags" />
        </DhfField>

        {/* v9 · Do's & Don'ts — visit-level, one row between Red flags and Follow-up */}
        <DhfField label="Instructions : Do's (one per line)" dpd>
          <textarea rows={2} value={(form.dos || []).join('\n')}
            onChange={e => setField('dos', e.target.value.split('\n'))}
            placeholder={'Drink plenty of warm fluids\nRest well and monitor temperature'}
            className="w-full rounded-lg border border-emerald-200 px-2.5 py-2 text-sm resize-y focus:ring-2 focus:ring-emerald-200" data-testid="dhf-dos" />
        </DhfField>
        <DhfField label="Instructions : Don'ts (one per line)" dpd>
          <textarea rows={2} value={(form.donts || []).join('\n')}
            onChange={e => setField('donts', e.target.value.split('\n'))}
            placeholder={'Avoid cold drinks and outdoor exposure\nAvoid strenuous activity'}
            className="w-full rounded-lg border border-rose-200 px-2.5 py-2 text-sm resize-y focus:ring-2 focus:ring-rose-200" data-testid="dhf-donts" />
        </DhfField>
        <DhfField label="Follow-up plan" full dpd>
          <input value={form.follow_up_plan} onChange={e => onFollowUpChange(e.target.value)}
            placeholder="e.g. Review after 7 days · Repeat tests on day 14"
            className="w-full rounded-lg border border-slate-200 px-2.5 py-2 text-sm" data-testid="dhf-followup" />
          {form.review_date_iso && (
            <p className="text-[10px] text-emerald-700 mt-1 flex items-center gap-1" data-testid="dhf-review-date-chip">
              <CheckCircle2 size={10} /> Auto-parsed · patient PWA fires a green-dot reminder on <b>{form.review_date_iso}</b>
            </p>
          )}
          {voiceEnabled && voiceQuota?.activated && (
            <div className="mt-1.5">
              <VoiceMicHook target="followup" label="Dictate follow-up" onParsed={applyVoicedFollowup} onUsed={() => { usedVoiceRef.current = true; }} />
            </div>
          )}
        </DhfField>

        {/* Turbo-only: handwritten prescription image (1 page max, no OCR) */}
        {quota?.doctor_tier === 'turbo' && (
          <DhfField label="Handwritten prescription photo · Turbo" full>
            <div className="rounded-xl border border-amber-200 bg-amber-50/40 p-3" data-testid="dhf-rx-upload-block">
              <p className="text-[11px] text-amber-900 leading-relaxed flex items-start gap-1.5">
                <Crown size={12} className="text-amber-600 mt-0.5 shrink-0" />
                <span><b>Only 1 page allowed</b> — we&apos;re building the rest inside HealthifyX. Image auto-compresses on your device before upload (WhatsApp-style).</span>
              </p>
              {form.handwritten_rx_url ? (
                <div className="mt-3 flex items-start gap-3">
                  <img src={form.handwritten_rx_url} alt="Handwritten Rx" className="w-28 h-36 object-cover rounded-lg border border-amber-300" data-testid="dhf-rx-preview" />
                  <div className="flex flex-col gap-1.5">
                    <button onClick={() => setField('handwritten_rx_url', '')}
                      className="text-[11px] text-rose-600 hover:text-rose-700 font-semibold text-left" data-testid="dhf-rx-remove">
                      Remove & re-shoot
                    </button>
                    <p className="text-[10px] text-slate-500 leading-snug">Attached to this DHF. Patient sees it alongside the typed Rx.</p>
                  </div>
                </div>
              ) : (
                <label className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500 text-white text-xs font-semibold hover:bg-amber-600 cursor-pointer" data-testid="dhf-rx-upload-btn">
                  <Plus size={11} /> {rxUploading ? 'Uploading…' : 'Take / upload 1 photo'}
                  <input type="file" accept="image/*" capture="environment" className="hidden" onChange={onRxPicked} disabled={rxUploading} />
                </label>
              )}
              {rxErr && <p className="text-[11px] text-rose-600 mt-2">{rxErr}</p>}
            </div>
          </DhfField>
        )}
      </div>

      {err && <p className="text-xs text-rose-600 mt-2">{err}</p>}

      {/* NEW · Short Description — patient-facing summary auto-composed from filled fields.
          Stops auto-updating the moment the doctor types manually (detected via manualShortDescRef). */}
      <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-3" data-testid="dhf-short-desc-block">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-700">Short description · what the patient reads first</p>
          <span className="text-[9px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800 border border-emerald-200 px-1.5 py-0.5 rounded-full">AUTO · EDITABLE</span>
          <span className="ml-auto text-[9px] font-bold uppercase tracking-wider bg-sky-100 text-sky-800 border border-sky-200 px-1.5 py-0.5 rounded-full flex items-center gap-1"><Lock size={8} /> DpD</span>
        </div>
        <div className="relative">
          <textarea
            rows={2}
            value={form.short_description}
            onChange={e => onShortDescChange(e.target.value.slice(0, 240))}
            placeholder="Fills automatically from your assessment, threads and Rx above. Edit any time — your edits stick."
            className="w-full rounded-lg border border-slate-200 px-2.5 py-2 text-sm resize-y pr-16"
            data-testid="dhf-short-description"
          />
          <span className="absolute right-2 bottom-2 text-[10px] font-semibold text-slate-500 tabular-nums">
            {(form.short_description || '').length} / 240
          </span>
        </div>
        <p className="text-[10px] text-slate-500 mt-1 leading-relaxed">
          {shortDescManualRef.current
            ? <>Manual edit mode · auto-fill paused. <button type="button" onClick={resumeAutoShortDesc} className="underline text-sky-700 font-semibold" data-testid="dhf-short-desc-resume-auto">Resume auto-fill</button></>
            : <>Auto-drafted from the fields above · type to override any time.</>}
        </p>
      </div>

      {/* CTA */}
      <button
        onClick={onGenerate}
        disabled={generating || !visitId}
        className="w-full mt-4 py-2.5 rounded-xl bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-50 flex flex-col items-center justify-center gap-0.5"
        data-testid="dhf-generate"
      >
        <span className="flex items-center gap-2 text-sm font-bold">
          {generating ? <Loader2 size={14} className="animate-spin" /> : <Shield size={14} />}
          {generating ? 'Generating sealed file…' : 'Generate Digital Health File'}
        </span>
        {!generating && (
          <span className="text-[10px] font-medium text-sky-100/90 tracking-wide">Final · Non-Editable · Send To Patient</span>
        )}
      </button>

      {/* Turbo upsell (founder copy) — shown only when patient WILL be charged */}
      {willCostPatient && quota?.turbo_upsell_message && (
        <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50/70 p-3" data-testid="dhf-turbo-upsell">
          <p className="text-[11px] text-amber-900 leading-relaxed flex items-start gap-1.5">
            <Crown size={12} className="text-amber-600 mt-0.5 shrink-0" />
            <span>{quota.turbo_upsell_message}</span>
          </p>
        </div>
      )}
    </div>
  );
}

function DhfField({ label, full, children, ppd, dpd }) {
  return (
    <div className={full ? 'md:col-span-2' : ''}>
      <div className="flex items-center gap-1.5 mb-1 flex-wrap">
        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</p>
        {ppd ? (
          <span className="text-[9px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-800 border border-emerald-200 px-1.5 py-0.5 rounded-full flex items-center gap-1" data-testid="dhf-ppd-chip">
            <Lock size={8} /> PpD · Patient-Provided
          </span>
        ) : dpd ? (
          <span className="text-[9px] font-bold uppercase tracking-wider bg-sky-100 text-sky-800 border border-sky-200 px-1.5 py-0.5 rounded-full flex items-center gap-1" data-testid="dhf-dpd-chip">
            <Lock size={8} /> DpD
          </span>
        ) : null}
      </div>
      {children}
    </div>
  );
}

// Fixed dosing patterns (morning · afternoon · night = 1/0). Choosing a
// pill also auto-populates a readable dose so the Dose field carries a
// plain-English hint the patient can grok.
const FREQ_PATTERNS = [
  { key: '1-1-1', readable: '1-1-1 · morning-afternoon-night' },
  { key: '1-0-0', readable: '1-0-0 · morning only' },
  { key: '1-0-1', readable: '1-0-1 · morning + night' },
  { key: '0-1-1', readable: '0-1-1 · afternoon + night' },
  { key: '0-0-1', readable: '0-0-1 · night only' },
];

// Meal-timing chips — doctor picks per patient; NOT auto-populated.
// Two-letter codes keep the row compact; expansion tooltip lives on hover.
const MEAL_TIMINGS = [
  { code: 'BB', label: 'Before Breakfast' },
  { code: 'AB', label: 'After Breakfast' },
  { code: 'BL', label: 'Before Lunch' },
  { code: 'AL', label: 'After Lunch' },
  { code: 'BD', label: 'Before Dinner' },
  { code: 'AD', label: 'After Dinner' },
];

// v3 · Dosage-form types (mockup-locked). Each form type carries a colour
// theme + a big-icon renderer so the doctor spots the form-factor from
// across the room. Colour tokens map to Tailwind classes below.
const MEDICINE_FORM_TYPES = [
  { key: 'tab',   label: 'TAB (TABLET)',    colorHex: '#0284c7', ring: 'border-sky-500',    head: 'bg-sky-600',    icon_svg: 'tab' },
  { key: 'cap',   label: 'CAP (CAPSULE)',   colorHex: '#7c3aed', ring: 'border-violet-500', head: 'bg-violet-600', icon_svg: 'cap' },
  { key: 'syrup', label: 'SYRUP',           colorHex: '#f59e0b', ring: 'border-amber-500',  head: 'bg-amber-600',  icon_svg: 'syrup' },
  { key: 'inj',   label: 'INJ (INJECTION)', colorHex: '#f43f5e', ring: 'border-rose-500',   head: 'bg-rose-600',   icon_emoji: '💉' },
  { key: 'drop',  label: 'DROP (DROPS)',    colorHex: '#0ea5e9', ring: 'border-sky-400',    head: 'bg-sky-500',    icon_emoji: '💧' },
  { key: 'ointmentgel',   label: 'OINTMENT · GEL',   colorHex: '#d97706', ring: 'border-amber-500',  head: 'bg-amber-600',  img: 'https://customer-assets-7cd3h4nn.emergentagent.net/job_180eab4d-2bcb-4a0a-9990-b7ee7474c7de/artifacts/34024n31_Screenshot%202026-08-01%20at%208.34.26%E2%80%AFAM.png' },
  { key: 'ointmentcream', label: 'OINTMENT · CREAM', colorHex: '#c2410c', ring: 'border-orange-500', head: 'bg-orange-600', img: 'https://customer-assets-7cd3h4nn.emergentagent.net/job_180eab4d-2bcb-4a0a-9990-b7ee7474c7de/artifacts/cu6dwzug_Screenshot%202026-08-01%20at%208.57.46%E2%80%AFAM.png' },
  { key: 'spray',   label: 'SPRAY',         colorHex: '#14b8a6', ring: 'border-teal-500',   head: 'bg-teal-600',   img: 'https://customer-assets-7cd3h4nn.emergentagent.net/job_180eab4d-2bcb-4a0a-9990-b7ee7474c7de/artifacts/45z8e7ar_Screenshot%202026-08-01%20at%208.31.33%E2%80%AFAM.png' },
  { key: 'gargle',  label: 'GARGLE',        colorHex: '#65a30d', ring: 'border-lime-500',   head: 'bg-lime-600',   img: 'https://customer-assets-7cd3h4nn.emergentagent.net/job_180eab4d-2bcb-4a0a-9990-b7ee7474c7de/artifacts/jwape2r9_Screenshot%202026-08-01%20at%208.30.24%E2%80%AFAM.png' },
  { key: 'sachet',  label: 'RINSE SACHETS', colorHex: '#a16207', ring: 'border-amber-600',  head: 'bg-amber-700',  img: 'https://customer-assets-7cd3h4nn.emergentagent.net/job_180eab4d-2bcb-4a0a-9990-b7ee7474c7de/artifacts/ksudecae_Screenshot%202026-08-01%20at%209.11.19%E2%80%AFAM.png' },
  { key: 'wash',    label: 'WASH',          colorHex: '#0e7490', ring: 'border-cyan-600',   head: 'bg-cyan-700',   icon_emoji: '🚿' },
  { key: 'iv',      label: 'IV · MEDICAL SUPERVISION', colorHex: '#dc2626', ring: 'border-red-500', head: 'bg-red-600', icon_emoji: '🚨' },
];

function _formTypeMeta(key) {
  return MEDICINE_FORM_TYPES.find(t => t.key === key) || null;
}

function FormTypeIcon({ meta, size = 44 }) {
  if (!meta) return null;
  if (meta.img) {
    return <img src={meta.img} alt={meta.label} style={{ width: size, height: size }} className="rounded-lg object-cover" />;
  }
  if (meta.icon_emoji) {
    return <span style={{ fontSize: size * 0.7, lineHeight: 1 }}>{meta.icon_emoji}</span>;
  }
  if (meta.icon_svg === 'tab') {
    return (
      <svg viewBox="0 0 64 64" width={size} height={size}>
        <circle cx="32" cy="32" r="26" fill="#7dd3fc" stroke="#0284c7" strokeWidth="3"/>
        <line x1="8" y1="32" x2="56" y2="32" stroke="#0369a1" strokeWidth="3" strokeLinecap="round"/>
      </svg>
    );
  }
  if (meta.icon_svg === 'cap') {
    return (
      <svg viewBox="0 0 64 64" width={size} height={size}>
        <g transform="rotate(-30 32 32)">
          <rect x="10" y="24" width="44" height="16" rx="8" fill="#c4b5fd" stroke="#6d28d9" strokeWidth="3"/>
          <rect x="10" y="24" width="22" height="16" rx="8" fill="#ede9fe"/>
        </g>
      </svg>
    );
  }
  if (meta.icon_svg === 'syrup') {
    return (
      <svg viewBox="0 0 64 64" width={size} height={size}>
        <rect x="24" y="6" width="16" height="8" rx="1.5" fill="#b45309"/>
        <path d="M22 14 L42 14 L44 22 L44 54 Q44 58 40 58 L24 58 Q20 58 20 54 L20 22 Z" fill="#fde68a" stroke="#b45309" strokeWidth="2.5"/>
        <path d="M20 32 L44 32 L44 54 Q44 58 40 58 L24 58 Q20 58 20 54 Z" fill="#f59e0b"/>
      </svg>
    );
  }
  return null;
}

function MedicineRow({ m, idx, onUpdate, onUpdateMany, onRemove, onLearn, conditions = [] }) {
  const [showCrossPicker, setShowCrossPicker] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  // Default to TAB when form_type is missing (legacy rows, cross-device sync)
  const effectiveFormType = m.form_type || 'tab';
  const formMeta = _formTypeMeta(effectiveFormType);
  const primaryCond = (conditions || []).find(c => c.id === m.condition_id);
  const crossConds = (conditions || []).filter(c => (m.cross_condition_ids || []).includes(c.id));

  // ── Auto-quantity: multiply dose × frequency-positions × duration-days ──
  // Understands: dose "1 tab", "2 cap", "10ml", "1"; frequency "1-0-1",
  // "1-1-1", "1-0-0-1"; duration "5 days", "10 day", "3d". Only fills the
  // field when the doctor hasn't manually entered a quantity, and only when
  // all three inputs are present. Editable — the doctor's edit wins.
  useEffect(() => {
    // Skip if doctor already typed a quantity manually
    if ((m.quantity || '').trim() && (m.quantity || '').trim() !== (m._autoQuantity || '')) return;
    const doseNum = parseFloat((m.dose || '').match(/[\d.]+/)?.[0] || '');
    const freqDigits = (m.frequency || '').match(/\d+/g) || [];
    const freqPerDay = freqDigits.reduce((s, d) => s + parseFloat(d), 0);
    const durNum = parseFloat((m.duration || '').match(/\d+/)?.[0] || '');
    if (doseNum > 0 && freqPerDay > 0 && durNum > 0) {
      const total = doseNum * freqPerDay * durNum;
      const isRounded = Number.isInteger(total);
      const unit = /ml/i.test(m.dose || '') ? 'ml'
                 : ((m.form_type === 'cap') ? 'cap' : 'tab');
      const qStr = `${isRounded ? total : total.toFixed(1)} ${unit}`;
      if (m.quantity !== qStr) {
        onUpdate(idx, 'quantity', qStr);
        onUpdate(idx, '_autoQuantity', qStr);  // mark it as auto-computed
      }
    }
  }, [m.dose, m.frequency, m.duration, m.form_type]);

  const toggleTiming = (code) => {
    const cur = Array.isArray(m.meal_timings) ? m.meal_timings : [];
    const next = cur.includes(code) ? cur.filter(c => c !== code) : [...cur, code];
    onUpdate(idx, 'meal_timings', next);
  };
  const pickFrequency = (key, readable) => onUpdate(idx, 'frequency', readable);
  const pickForm = (k) => onUpdate(idx, 'form_type', k);
  const toggleCross = (condId) => {
    const cur = Array.isArray(m.cross_condition_ids) ? m.cross_condition_ids : [];
    const next = cur.includes(condId) ? cur.filter(c => c !== condId) : [...cur, condId];
    onUpdate(idx, 'cross_condition_ids', next);
  };

  // Colour of the super-border + head bar. Falls back to plain slate when
  // no form-type is chosen — so legacy rows stay pristine.
  const outerCls = formMeta ? `border-2 ${formMeta.ring}` : 'border border-slate-200';

  return (
    <div className={`rounded-lg bg-white ${outerCls} overflow-hidden relative`} data-testid={`dhf-med-row-${idx}`}>
      {/* Coloured head bar showing current form type + cross-condition chip */}
      <div className={`flex items-center gap-2 px-2 py-1 ${formMeta ? formMeta.head + ' text-white' : 'bg-slate-50'}`}>
        <span
          className="text-[10px] font-bold uppercase tracking-widest bg-white/20 border border-white/40 rounded px-2 py-0.5"
          data-testid={`dhf-med-form-tab-${idx}`}
        >
          {formMeta ? formMeta.label : 'TAB (TABLET)'}
        </span>
        {primaryCond && (
          <span className="text-[9.5px] font-bold uppercase tracking-wider bg-white/15 border border-white/30 rounded-full px-2 py-0.5" data-testid={`dhf-med-owner-${idx}`}>
            💊 For: {(primaryCond.name || 'Untitled').slice(0, 22)}{(primaryCond.name || '').length > 22 ? '…' : ''}
          </span>
        )}
        {(conditions || []).length > 1 && (
          <button
            type="button"
            onClick={() => setShowCrossPicker(v => !v)}
            className={`text-[9.5px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 flex items-center gap-1 ${
              crossConds.length ? 'bg-amber-100 text-amber-800 border border-amber-300' : 'bg-white/10 text-white border border-white/30 hover:bg-white/20'
            } ${formMeta ? '' : 'text-slate-600 bg-white border-slate-300'}`}
            data-testid={`dhf-med-cross-btn-${idx}`}
          >
            {crossConds.length ? `Also for: ${crossConds.map(c => (c.name || 'Untitled').slice(0, 12)).join(', ')}` : '＋ Also for…'}
          </button>
        )}
        {!primaryCond && (conditions || []).length >= 1 && (
          <select
            value={m.condition_id || ''}
            onChange={e => onUpdate(idx, 'condition_id', e.target.value)}
            className="text-[10px] font-bold uppercase tracking-wider bg-white/15 border border-white/30 rounded px-2 py-0.5 text-white"
            data-testid={`dhf-med-owner-picker-${idx}`}
          >
            <option value="" className="text-slate-700">＋ Tag to condition</option>
            {(conditions || []).map(c => (
              <option key={c.id} value={c.id} className="text-slate-700">{c.name || 'Untitled'}</option>
            ))}
          </select>
        )}
        <div className="ml-auto flex items-center gap-1">
          <button onClick={() => onRemove(idx)} className="p-1 rounded text-white/90 hover:text-white hover:bg-white/10" data-testid={`dhf-med-rm-${idx}`}>
            <X size={12} />
          </button>
        </div>
      </div>

      {/* Form-type chip row — ALL 12 always visible, click to select */}
      <div className="border-b border-slate-200 bg-slate-50 px-2 py-1.5 flex flex-wrap gap-1" data-testid={`dhf-med-form-chips-${idx}`}>
        {MEDICINE_FORM_TYPES.map(t => (
          <button
            key={t.key}
            type="button"
            onClick={() => pickForm(t.key)}
            className={`text-[9.5px] font-bold uppercase tracking-wider rounded px-1.5 py-0.5 border transition ${effectiveFormType === t.key ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-300 hover:border-slate-500'}`}
            data-testid={`dhf-med-form-opt-${idx}-${t.key}`}
            style={{ color: effectiveFormType === t.key ? '#fff' : t.colorHex }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Cross-condition picker popover */}
      {showCrossPicker && (
        <div className="border-b border-slate-200 bg-amber-50/40 p-2 flex flex-wrap gap-1" data-testid={`dhf-med-cross-picker-${idx}`}>
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 self-center mr-1">Also for:</span>
          {(conditions || []).filter(c => c.id !== m.condition_id).map(c => {
            const active = (m.cross_condition_ids || []).includes(c.id);
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => toggleCross(c.id)}
                className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-1 border ${active ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-slate-700 border-slate-300 hover:border-amber-400'}`}
                data-testid={`dhf-med-cross-opt-${idx}-${c.id}`}
              >
                {c.name || 'Untitled'}
              </button>
            );
          })}
          {(conditions || []).filter(c => c.id !== m.condition_id).length === 0 && (
            <span className="text-[10px] text-slate-500 italic">Add a second condition above to cross-link this medicine.</span>
          )}
        </div>
      )}

      {/* Main body — icon on left + fields */}
      <div className="flex items-stretch gap-2 p-2">
        {formMeta && (
          <div className="flex-shrink-0 w-16 flex items-center justify-center" data-testid={`dhf-med-icon-${idx}`}>
            <FormTypeIcon meta={formMeta} size={54} />
          </div>
        )}
        <div className="flex-1 min-w-0 space-y-1.5">
          {/* Row 1 — Name / Strength / Dose / Duration */}
          <div className="grid grid-cols-[1.4fr_0.9fr_0.9fr_0.9fr] gap-1.5 items-center">
            <input value={m.name || ''} onChange={e => onUpdate(idx, 'name', e.target.value)}
              onBlur={async (e) => {
                const brand = (e.target.value || '').trim();
                onLearn('medicine_name', brand);
                if (!brand || brand.length < 2) return;
                setAiBusy(true);
                // Batch all AI-derived fields into a single state update so
                // React's state-batching doesn't drop earlier writes.
                const patch = {};
                try {
                  const { data: pref } = await api.get(`/dashboard/medicines/preferences?brand=${encodeURIComponent(brand)}`);
                  if (pref?.found) {
                    if (!m.dose)      patch.dose = pref.dose || '';
                    if (!m.frequency) patch.frequency = pref.frequency || '';
                    if (!m.duration)  patch.duration = pref.duration || '';
                    if (!m.strength)  patch.strength = pref.strength || '';
                    if (!m.notes)     patch.notes = pref.notes || '';
                    if (pref.form_type && !m.form_type) patch.form_type = pref.form_type;
                    if ((pref.meal_timings || []).length && !(m.meal_timings || []).length) {
                      patch.meal_timings = pref.meal_timings;
                    }
                  }
                } catch (_e) { /* silent */ }
                try {
                  const { data: cls } = await api.post('/dashboard/medicines/classify', { brand });
                  if (cls?.molecule && !m.molecule)     patch.molecule = cls.molecule;
                  if (cls?.drug_class && !m.drug_class) patch.drug_class = cls.drug_class;
                  if (cls?.default_strength && !m.strength && !patch.strength) patch.strength = cls.default_strength;
                  if (cls?.schedule && !m.schedule)     patch.schedule = cls.schedule;
                } catch (_e) { /* silent — offline / LLM key missing */ }
                if (Object.keys(patch).length) onUpdateMany(idx, patch);
                setAiBusy(false);
              }}
              list="dhf-vocab-medicine" placeholder="Medicine" autoComplete="off"
              className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs" data-testid={`dhf-med-name-${idx}`} />
            <input value={m.strength || ''} onChange={e => onUpdate(idx, 'strength', e.target.value)}
              onBlur={e => onLearn('strength', e.target.value)}
              list="dhf-vocab-strength" placeholder="Strength" autoComplete="off"
              className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs" data-testid={`dhf-med-strength-${idx}`} />
            <input value={m.dose || ''} onChange={e => onUpdate(idx, 'dose', e.target.value)}
              onBlur={e => onLearn('dose', e.target.value)}
              list="dhf-vocab-dose" placeholder="Dose" autoComplete="off"
              className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs" data-testid={`dhf-med-dose-${idx}`} />
            <input value={m.duration || ''} onChange={e => onUpdate(idx, 'duration', e.target.value)}
              onBlur={e => onLearn('duration', e.target.value)}
              list="dhf-vocab-duration" placeholder="Duration" autoComplete="off"
              className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs" data-testid={`dhf-med-duration-${idx}`} />
          </div>

          {/* Row 1b — AI-classified molecule + drug class + regulatory schedule.
              Auto-filled on brand entry (blur), editable — the doctor's edits
              are what print on the Rx. */}
          {(m.molecule || m.drug_class || m.schedule || m.name || aiBusy) && (
            <div className="grid grid-cols-[1fr_1fr_0.7fr] gap-1.5 items-center relative">
              <input value={m.molecule || ''} onChange={e => onUpdate(idx, 'molecule', e.target.value)}
                placeholder={aiBusy ? 'AI thinking…' : 'Molecule (AI auto-fills)'} autoComplete="off"
                className={`rounded-lg border border-slate-200 px-2 py-1 text-[11px] italic text-slate-700 ${aiBusy ? 'bg-sky-50 animate-pulse' : ''}`}
                data-testid={`dhf-med-molecule-${idx}`} />
              <input value={m.drug_class || ''} onChange={e => onUpdate(idx, 'drug_class', e.target.value)}
                placeholder={aiBusy ? 'AI thinking…' : 'Drug class (AI auto-fills)'} autoComplete="off"
                className={`rounded-lg border border-slate-200 px-2 py-1 text-[11px] italic text-slate-500 ${aiBusy ? 'bg-sky-50 animate-pulse' : ''}`}
                data-testid={`dhf-med-drugclass-${idx}`} />
              <input value={m.schedule || ''} onChange={e => onUpdate(idx, 'schedule', e.target.value)}
                placeholder={aiBusy ? 'AI thinking…' : 'Schedule (H / OTC)'} autoComplete="off"
                className={`rounded-lg border border-slate-200 px-2 py-1 text-[11px] italic text-slate-500 ${aiBusy ? 'bg-sky-50 animate-pulse' : ''}`}
                data-testid={`dhf-med-schedule-${idx}`} />
            </div>
          )}

          {/* Row 1c — Auto-computed total quantity. Editable — the doctor's
              override is what prints on the Rx. Recomputes automatically when
              dose / frequency / duration / form_type change (see useEffect). */}
          {(m.dose || m.frequency || m.duration) && (
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                Total Quantity
              </span>
              <input value={m.quantity || ''} onChange={e => { onUpdate(idx, 'quantity', e.target.value); onUpdate(idx, '_autoQuantity', ''); }}
                placeholder="auto (dose × freq × days)" autoComplete="off"
                className="rounded-lg border border-slate-200 px-2 py-1 text-[11px] w-32 text-slate-700 font-semibold"
                data-testid={`dhf-med-quantity-${idx}`} />
              <span className="text-[10px] text-slate-400 italic">
                {m.quantity && m.quantity === m._autoQuantity ? '· auto-computed, editable' : ''}
              </span>
            </div>
          )}

          {/* Row 2 — Frequency pill picker + custom override */}
          <div className="flex flex-wrap items-center gap-1">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mr-1">Frequency</span>
            {FREQ_PATTERNS.map(p => {
              const active = (m.frequency || '').startsWith(p.key);
              return (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => pickFrequency(p.key, p.readable)}
                  className={`px-2 py-0.5 rounded-full text-[11px] font-semibold border transition-colors ${active ? 'bg-sky-600 text-white border-sky-600' : 'bg-white text-slate-700 border-slate-300 hover:border-sky-300'}`}
                  data-testid={`dhf-freq-${idx}-${p.key}`}
                  title={p.readable}
                >
                  {p.key}
                </button>
              );
            })}
            <input value={m.frequency || ''} onChange={e => onUpdate(idx, 'frequency', e.target.value)}
              placeholder="Custom (editable)" autoComplete="off"
              className="flex-1 min-w-[140px] rounded-lg border border-slate-200 px-2 py-1 text-[11px]"
              data-testid={`dhf-med-frequency-${idx}`} />
          </div>

          {/* Row 3 — Meal-timing chips */}
          <div className="flex flex-wrap items-center gap-1">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mr-1">Timing</span>
            {MEAL_TIMINGS.map(mt => {
              const active = (m.meal_timings || []).includes(mt.code);
              return (
                <button
                  key={mt.code}
                  type="button"
                  onClick={() => toggleTiming(mt.code)}
                  title={mt.label}
                  className={`px-2 py-0.5 rounded-full text-[10px] font-bold border transition-colors ${active ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-slate-600 border-slate-300 hover:border-emerald-400'}`}
                  data-testid={`dhf-timing-${idx}-${mt.code}`}
                >
                  {mt.code}
                </button>
              );
            })}
          </div>

          {/* Row 4 — freeform Notes */}
          <input value={m.notes || ''} onChange={e => onUpdate(idx, 'notes', e.target.value)}
            placeholder="Notes (freeform · not auto-suggested)" autoComplete="off"
            className="w-full rounded-lg border border-slate-200 px-2 py-1.5 text-xs"
            data-testid={`dhf-med-notes-${idx}`} />

          {/* ── Contributes to → milestone linkage (per medicine) ────────
              Doctor ticks 0..N of the primary condition's improvement
              milestones. Same milestone can be ticked by multiple meds
              (combination therapy). Timeline + patient card will render
              "driven by N medicines" via these ids. */}
          {primaryCond && (primaryCond.milestones || []).length > 0 && (
            <div className="rounded-lg border border-dashed border-violet-300 bg-violet-50/30 px-2 py-1.5" data-testid={`dhf-med-milestones-${idx}`}>
              <p className="text-[9px] font-bold uppercase tracking-widest text-violet-800 mb-1">🎯 Contributes to</p>
              <div className="flex flex-wrap gap-1">
                {(primaryCond.milestones || []).map(ms => {
                  const active = (m.milestone_ids || []).includes(ms.id);
                  const label = (ms.label || '').trim() || `Milestone ${ms.id.slice(-4)}`;
                  return (
                    <button
                      key={ms.id}
                      type="button"
                      onClick={() => {
                        const cur = Array.isArray(m.milestone_ids) ? m.milestone_ids : [];
                        const next = cur.includes(ms.id) ? cur.filter(x => x !== ms.id) : [...cur, ms.id];
                        onUpdate(idx, 'milestone_ids', next);
                      }}
                      className={`text-[10px] font-semibold rounded-full px-2 py-0.5 border ${active ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-violet-800 border-violet-300 hover:bg-violet-100'}`}
                      title={ms.signal ? `signal: ${ms.signal}` : ''}
                      data-testid={`dhf-med-milestone-chip-${idx}-${ms.id}`}
                    >
                      {label} · {ms.weeks || 2}w
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ─── AllergyAdder ─────────────────────────────────────────────────────
// Minimal add-a-chip UX. Doctor types label, picks severity, hits Enter.
function AllergyAdder({ onAdd }) {
  const [label, setLabel] = useState('');
  const [sev, setSev] = useState('mild');
  const submit = () => {
    const l = label.trim();
    if (!l) return;
    onAdd({ label: l, severity: sev });
    setLabel(''); setSev('mild');
  };
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-dashed border-rose-300 bg-white px-2 py-1" data-testid="dhf-allergy-adder">
      <input
        value={label}
        onChange={e => setLabel(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); submit(); } }}
        placeholder="+ Add allergy"
        className="text-[11px] outline-none w-24 bg-transparent"
        data-testid="dhf-allergy-input"
      />
      <select value={sev} onChange={e => setSev(e.target.value)}
        className="text-[9px] font-bold uppercase tracking-wider bg-white border border-slate-200 rounded px-1 py-0.5"
        data-testid="dhf-allergy-sev">
        <option value="mild">MILD</option>
        <option value="moderate">MODERATE</option>
        <option value="severe">SEVERE</option>
      </select>
      <button type="button" onClick={submit} className="text-rose-700 text-[11px] font-bold" data-testid="dhf-allergy-add">＋</button>
    </span>
  );
}

// ─── ConditionBlockRow ────────────────────────────────────────────────
// Coloured card per condition with its own symptoms · assessment ·
// tests · red flags · follow-up. Optional ICD-11 + SNOMED lookup.
function ConditionBlockRow({ cond, idx, color, onUpdate, onRemove, isReturning = false, linkedMedCount = 0 }) {
  const [symDraft, setSymDraft] = useState('');
  const [codeQ, setCodeQ] = useState('');
  const [codeHits, setCodeHits] = useState([]);
  const [showCodes, setShowCodes] = useState(false);
  const palette = {
    sky:    { border:'border-sky-300',    bg:'bg-sky-50/40',    head:'bg-sky-600',    text:'text-sky-800',    ring:'ring-sky-200'  },
    amber:  { border:'border-amber-300',  bg:'bg-amber-50/40',  head:'bg-amber-600',  text:'text-amber-800',  ring:'ring-amber-200'},
    violet: { border:'border-violet-300', bg:'bg-violet-50/40', head:'bg-violet-600', text:'text-violet-800', ring:'ring-violet-200'},
  }[color] || {};
  const searchCodes = async (q) => {
    setCodeQ(q);
    if ((q || '').trim().length < 2) { setCodeHits([]); return; }
    try {
      const { data } = await api.get('/dashboard/clinical-codes/search', { params: { q } });
      setCodeHits(data.results || []);
    } catch (_) { setCodeHits([]); }
  };
  const pickCode = (c) => {
    onUpdate({ name: c.display, icd11_code: c.icd11, snomed_code: c.snomed });
    setCodeQ(''); setCodeHits([]); setShowCodes(false);
  };
  const addSymptom = () => {
    const s = symDraft.trim();
    if (!s) return;
    onUpdate({ symptoms: [...(cond.symptoms || []), s] });
    setSymDraft('');
  };
  return (
    <div className={`rounded-xl border-2 ${palette.border} bg-white overflow-hidden ${(cond.status === 'cured' || cond.status === 'concluded') ? 'opacity-70' : ''}`} data-testid={`dhf-condition-${idx}`}>
      <div className={`${palette.head} text-white px-3 py-2 flex items-center gap-2`}>
        <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-xs font-bold shrink-0">{idx + 1}</span>
        <input
          value={cond.name || ''}
          onChange={e => onUpdate({ name: e.target.value })}
          onFocus={() => { setShowCodes(true); if (cond.name) searchCodes(cond.name); }}
          placeholder="Health thread name (e.g. Viral URI, Hypertension)"
          className="flex-1 min-w-0 rounded-md px-2 py-1 bg-white/15 border border-white/30 text-white placeholder-white/70 text-sm font-semibold"
          data-testid={`dhf-condition-name-${idx}`}
        />
        {cond.icd11_code && (
          <span className="text-[9px] font-bold uppercase tracking-wider bg-white/20 border border-white/40 rounded-full px-2 py-0.5 whitespace-nowrap" data-testid={`dhf-condition-icd-${idx}`}>
            ICD-11 · {cond.icd11_code}
          </span>
        )}
        {/* Health-thread status pill · lifecycle: open → in_progress → cured / concluded */}
        <select
          value={cond.status || 'open'}
          onChange={e => onUpdate({ status: e.target.value })}
          className="text-[9px] font-bold uppercase tracking-wider bg-white/20 border border-white/40 rounded-full px-2 py-0.5 text-white cursor-pointer"
          data-testid={`dhf-condition-status-${idx}`}
          title="Longitudinal status DocSite tracks across visits"
        >
          <option value="open" className="text-slate-700">🟢 OPEN</option>
          <option value="in_progress" className="text-slate-700">🟡 IN PROGRESS</option>
          <option value="cured" className="text-slate-700">✅ CURED</option>
          <option value="concluded" className="text-slate-700">🔒 CONCLUDED</option>
        </select>
        <span className="text-[9px] font-bold uppercase tracking-wider bg-white/20 border border-white/40 rounded-full px-2 py-0.5 flex items-center gap-1"><Lock size={8} /> DpD</span>
        <button onClick={onRemove} className="text-white/85 hover:text-white text-lg leading-none px-1" data-testid={`dhf-condition-rm-${idx}`}>×</button>
      </div>
      <div className="p-3 space-y-2">
        {/* Inline ICD-11 / SNOMED search */}
        {showCodes && (
          <div className="rounded-lg border border-slate-200 bg-white p-2" data-testid={`dhf-condition-codes-${idx}`}>
            <input
              value={codeQ}
              onChange={e => searchCodes(e.target.value)}
              placeholder="Search ICD-11 / SNOMED-CT · e.g. viral, hypertension, diabetes"
              className="w-full rounded border border-slate-200 px-2 py-1 text-xs"
              data-testid={`dhf-condition-code-search-${idx}`}
            />
            {codeHits.length > 0 && (
              <div className="mt-1 max-h-32 overflow-y-auto text-[11px]">
                {codeHits.map((c) => (
                  <button
                    key={c.icd11 + c.snomed}
                    type="button"
                    onClick={() => pickCode(c)}
                    className="w-full text-left px-2 py-1 rounded hover:bg-slate-100 flex items-center gap-2"
                    data-testid={`dhf-condition-code-pick-${idx}-${c.icd11}`}
                  >
                    <span className={`${palette.text} font-mono text-[10px]`}>{c.icd11}</span>
                    <span className="text-slate-700 truncate">{c.display}</span>
                  </button>
                ))}
              </div>
            )}
            <p className="text-[9px] text-slate-400 mt-1">Optional · maps to WHO ICD-11 + SNOMED-CT for episode-matching + interoperability.</p>
          </div>
        )}

        {/* Symptoms chip row */}
        <div>
          <p className="text-[9px] font-bold uppercase tracking-wider text-slate-500 mb-1">Related symptoms</p>
          <div className="flex flex-wrap items-center gap-1.5">
            {(cond.symptoms || []).map((s, si) => (
              <span key={si} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 border border-slate-200 text-[11px] text-slate-700" data-testid={`dhf-condition-sym-${idx}-${si}`}>
                {s}
                <button onClick={() => onUpdate({ symptoms: cond.symptoms.filter((_, x) => x !== si) })} className="text-slate-500">×</button>
              </span>
            ))}
            <span className="inline-flex items-center gap-1 rounded-full border border-dashed border-slate-300 bg-white px-2 py-0.5">
              <input
                value={symDraft}
                onChange={e => setSymDraft(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addSymptom(); } }}
                placeholder="+ Add symptom"
                className="text-[11px] outline-none w-24 bg-transparent"
                data-testid={`dhf-condition-sym-input-${idx}`}
              />
              <button type="button" onClick={addSymptom} className="text-slate-600 text-[11px] font-bold" data-testid={`dhf-condition-sym-add-${idx}`}>＋</button>
            </span>
          </div>
        </div>

        {/* Assessment note (per condition) */}
        <div>
          <p className="text-[9px] font-bold uppercase tracking-wider text-slate-500 mb-1">Assessment note (this condition only)</p>
          <textarea
            rows={2}
            value={cond.assessment_note || ''}
            onChange={e => onUpdate({ assessment_note: e.target.value })}
            placeholder="e.g. No exudate on exam. Likely self-limiting."
            className="w-full rounded-lg border border-slate-200 px-2 py-1.5 text-xs resize-y"
            data-testid={`dhf-condition-note-${idx}`}
          />
        </div>

        {/* NEW (Aug 2026) · OPD procedure done in THIS consult. Distinct
            from Past Surgeries on the patient baseline — this is what the
            doctor did today (e.g. suture removal, minor I&D, joint
            aspiration). Per-visit, sits inside the condition block. */}
        <div className="grid md:grid-cols-2 gap-2" data-testid={`dhf-opd-procedure-${idx}`}>
          <div>
            <p className="text-[9px] font-bold uppercase tracking-wider text-slate-500 mb-1">OPD procedure done this visit</p>
            <input
              value={cond.opd_procedure_name || ''}
              onChange={e => onUpdate({ opd_procedure_name: e.target.value })}
              placeholder="e.g. Suture removal · I&D · Joint aspiration"
              className="w-full rounded-lg border border-slate-200 px-2 py-1.5 text-xs"
              data-testid={`dhf-opd-name-${idx}`}
            />
          </div>
          <div>
            <p className="text-[9px] font-bold uppercase tracking-wider text-slate-500 mb-1">OPD procedure notes</p>
            <input
              value={cond.opd_procedure_notes || ''}
              onChange={e => onUpdate({ opd_procedure_notes: e.target.value })}
              placeholder="Brief · site, tolerance, dressing"
              className="w-full rounded-lg border border-slate-200 px-2 py-1.5 text-xs"
              data-testid={`dhf-opd-notes-${idx}`}
            />
          </div>
        </div>

        {/* Tests + Red-flags + Follow-up (per-thread, so each condition tracks its own escalation triggers) */}
        <div className="grid md:grid-cols-2 gap-2">
          <div>
            <p className="text-[9px] font-bold uppercase tracking-wider text-slate-500 mb-1">Tests advised</p>
            <textarea
              rows={2}
              value={(cond.tests_advised || []).join('\n')}
              onChange={e => onUpdate({ tests_advised: e.target.value.split('\n') })}
              placeholder="Optional · one per line"
              className="w-full rounded-lg border border-slate-200 px-2 py-1.5 text-xs resize-y"
              data-testid={`dhf-condition-tests-${idx}`}
            />
          </div>
          <div>
            <p className="text-[9px] font-bold uppercase tracking-wider text-slate-500 mb-1">Red flags · when to seek help</p>
            <textarea
              rows={2}
              value={(cond.red_flags || []).join('\n')}
              onChange={e => onUpdate({ red_flags: e.target.value.split('\n') })}
              placeholder="Optional · one per line"
              className="w-full rounded-lg border border-slate-200 px-2 py-1.5 text-xs resize-y"
              data-testid={`dhf-condition-redflags-${idx}`}
            />
          </div>
          <div className="md:col-span-2">
            <p className="text-[9px] font-bold uppercase tracking-wider text-slate-500 mb-1">Follow-up</p>
            <input
              value={cond.follow_up_plan || ''}
              onChange={e => onUpdate({ follow_up_plan: e.target.value })}
              placeholder="e.g. Review after 5 days"
              className="w-full rounded-lg border border-slate-200 px-2 py-1.5 text-xs"
              data-testid={`dhf-condition-followup-${idx}`}
            />
          </div>

          {/* v9 · Per-thread Do's / Don'ts — scoped to THIS condition.
              Rendered on the patient DHF under the condition it belongs to. */}
          <div>
            <p className="text-[9px] font-bold uppercase tracking-wider text-slate-500 mb-1">Instructions : Do&rsquo;s <span className="normal-case font-normal text-slate-400 tracking-normal">(this condition · one per line)</span></p>
            <textarea
              rows={2}
              value={(cond.dos || []).join('\n')}
              onChange={e => onUpdate({ dos: e.target.value.split('\n') })}
              placeholder={'Quit smoking gradually with support\nUse a humidifier at night'}
              className="w-full rounded-lg border border-emerald-200 px-2 py-1.5 text-xs resize-y focus:ring-2 focus:ring-emerald-200"
              data-testid={`dhf-condition-dos-${idx}`}
            />
          </div>
          <div>
            <p className="text-[9px] font-bold uppercase tracking-wider text-slate-500 mb-1">Instructions : Don&rsquo;ts <span className="normal-case font-normal text-slate-400 tracking-normal">(this condition · one per line)</span></p>
            <textarea
              rows={2}
              value={(cond.donts || []).join('\n')}
              onChange={e => onUpdate({ donts: e.target.value.split('\n') })}
              placeholder={'Avoid known seasonal triggers\nAvoid smoking in enclosed spaces'}
              className="w-full rounded-lg border border-rose-200 px-2 py-1.5 text-xs resize-y focus:ring-2 focus:ring-rose-200"
              data-testid={`dhf-condition-donts-${idx}`}
            />
          </div>

          {/* ── Expected Improvement Milestones (moved UP · Aug 2026) ─────
              Positioned right after Follow-up so the doctor declares the
              targets FIRST, and then the medicines below can immediately
              tick "Contributes to →" against them. */}
          <div className="md:col-span-2">
            <ConditionMilestonesEditor
              condIdx={idx}
              milestones={cond.milestones || []}
              onChange={(next) => onUpdate({ milestones: next })}
            />
          </div>

          {/* ── Linked-medicines strip (Aug 2026) ────────────────────────
              At-a-glance count of the medicines assigned to this condition
              via condition_id. When there are none yet, prompts the doctor
              to open a med row already pre-tagged with this condition. */}
          <div className="md:col-span-2 rounded-xl bg-slate-50 border border-slate-200 px-3 py-2 flex items-center justify-between flex-wrap gap-2" data-testid={`dhf-condition-linked-meds-${idx}`}>
            <span className="text-[11px] font-semibold text-slate-700">
              💊 Medicines linked to this condition · <b className="text-slate-900">{linkedMedCount}</b>
            </span>
            <span className="text-[10px] text-slate-500 italic">
              {linkedMedCount === 0 ? 'Scroll down and add a medicine — set the “for” field to this condition.' : 'See the medicines list below for details.'}
            </span>
          </div>

          {/* ── Visit progress capture (Aug 2026) ───────────────────────
              GATED on isReturning — only shows on V2+ visits for an
              existing thread. First-time thread creation has no prior
              visit to compare against, so these two fields stay hidden
              to keep the composer clean. */}
          {isReturning && (
            <div className="md:col-span-2">
              <p className="text-[9px] font-bold uppercase tracking-wider text-slate-500 mb-1">How is the patient this visit? (progress chip)</p>
              <div className="flex flex-wrap gap-1.5" data-testid={`dhf-condition-outcome-${idx}`}>
                {[
                  { key: 'improved', label: 'Improved', cls: 'emerald' },
                  { key: 'same',     label: 'Same',     cls: 'sky' },
                  { key: 'worse',    label: 'Worse',    cls: 'amber' },
                  { key: 'red_flag', label: 'Red flag triggered', cls: 'rose' },
                  { key: 'cured',    label: 'Cured',    cls: 'violet' },
                ].map(o => {
                  const active = cond.visit_outcome === o.key;
                  const activeCls = {
                    emerald: 'bg-emerald-600 text-white border-emerald-600',
                    sky:     'bg-sky-600 text-white border-sky-600',
                    amber:   'bg-amber-500 text-white border-amber-500',
                    rose:    'bg-rose-600 text-white border-rose-600',
                    violet:  'bg-violet-600 text-white border-violet-600',
                  }[o.cls];
                  return (
                    <button
                      key={o.key}
                      type="button"
                      onClick={() => onUpdate({ visit_outcome: active ? '' : o.key })}
                      className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border ${active ? activeCls : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'}`}
                      data-testid={`dhf-condition-outcome-chip-${idx}-${o.key}`}
                    >
                      {o.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {isReturning && (
            <div className="md:col-span-2">
              <p className="text-[9px] font-bold uppercase tracking-wider text-slate-500 mb-1">Doctor&apos;s observation this visit (follow-up narrative)</p>
              <textarea
                value={cond.doctor_inference || ''}
                onChange={e => onUpdate({ doctor_inference: e.target.value })}
                rows={2}
                placeholder="e.g. Cough returned 3 days after stopping paracetamol · likely secondary infection · switching to codeine syrup"
                className="w-full rounded-lg border border-slate-200 px-2 py-1.5 text-xs"
                data-testid={`dhf-condition-inference-${idx}`}
              />
            </div>
          )}

          {!isReturning && (
            <div className="md:col-span-2 rounded-lg bg-slate-50 border border-dashed border-slate-200 px-3 py-2" data-testid={`dhf-condition-new-thread-hint-${idx}`}>
              <p className="text-[10px] text-slate-500 italic">
                Progress chip &amp; doctor&apos;s observation unlock on the next visit — this is a fresh thread with nothing to compare against yet.
              </p>
            </div>
          )}

          {/* ── legacy anchor: milestone editor is now above (line ~1420).
               Kept this comment so search/replace history remains readable. */}
        </div>
      </div>
    </div>
  );
}

// ─── ConditionMilestonesEditor (inline · lightweight) ──────────────────
// Compact 1-line-per-row editor for improvement targets. Each row has:
//   • label · text
//   • weeks · pick 1..6 or type custom
//   • signal · optional short note (e.g. "lab upload", "patient tap")
// Milestone ids are generated locally (ms-<ts>-<rand>) so medicines below
// can reference them via milestone_ids[] BEFORE the DHF is sealed.
function ConditionMilestonesEditor({ condIdx, milestones, onChange }) {
  const add = () => onChange([...(milestones || []), { id: `ms-${Date.now()}-${Math.floor(Math.random()*999)}`, label: '', weeks: 2, signal: '' }]);
  const rm = (i) => onChange(milestones.filter((_, x) => x !== i));
  const upd = (i, patch) => onChange(milestones.map((x, j) => j === i ? { ...x, ...patch } : x));
  return (
    <div className="rounded-xl border border-dashed border-violet-300 bg-violet-50/30 p-2.5" data-testid={`dhf-condition-milestones-${condIdx}`}>
      <div className="flex items-center justify-between mb-1.5 flex-wrap gap-1">
        <p className="text-[9px] font-bold uppercase tracking-widest text-violet-800 flex items-center gap-1">
          🎯 Expected Improvement Milestones
        </p>
        <button type="button" onClick={add} className="text-[10px] font-semibold text-violet-800 hover:text-violet-900 inline-flex items-center gap-0.5" data-testid={`dhf-condition-milestone-add-${condIdx}`}>
          ＋ Add
        </button>
      </div>
      {!milestones?.length && (
        <p className="text-[10px] text-slate-500 italic px-0.5">Optional. Set 1-4 improvement targets — each medicine below can tick these as “contributes to”.</p>
      )}
      <ul className="space-y-1.5">
        {(milestones || []).map((m, i) => (
          <li key={m.id || i} className="grid grid-cols-1 md:grid-cols-[1.6fr_auto_1fr_auto] gap-1.5 md:items-center" data-testid={`dhf-condition-milestone-row-${condIdx}-${i}`}>
            <input
              value={m.label || ''}
              onChange={e => upd(i, { label: e.target.value })}
              placeholder="e.g. BP < 140/90"
              className="rounded-md border border-slate-200 px-2 py-1 text-[11px]"
              data-testid={`dhf-condition-milestone-label-${condIdx}-${i}`}
            />
            <div className="flex gap-0.5 flex-wrap">
              {[1,2,3,4,5,6].map(w => (
                <button key={w} type="button" onClick={() => upd(i, { weeks: w })}
                  className={`text-[10px] px-1.5 py-0.5 rounded border ${m.weeks === w ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-slate-600 border-slate-200 hover:bg-violet-50'}`}
                  data-testid={`dhf-condition-milestone-week-${condIdx}-${i}-${w}`}>{w}w</button>
              ))}
              <input type="number" min="1" max="26"
                value={![1,2,3,4,5,6].includes(m.weeks) ? m.weeks : ''}
                onChange={e => upd(i, { weeks: parseInt(e.target.value || '2', 10) })}
                placeholder="/"
                className="w-12 rounded border border-slate-200 px-1 py-0.5 text-[10px]" />
            </div>
            <input
              value={m.signal || ''}
              onChange={e => upd(i, { signal: e.target.value })}
              placeholder="signal (optional)"
              className="rounded-md border border-slate-200 px-2 py-1 text-[10px] text-slate-600"
              data-testid={`dhf-condition-milestone-signal-${condIdx}-${i}`}
            />
            <button type="button" onClick={() => rm(i)} className="text-rose-500 hover:text-rose-700 text-xs justify-self-end" title="Remove" data-testid={`dhf-condition-milestone-rm-${condIdx}-${i}`}>×</button>
          </li>
        ))}
      </ul>
    </div>
  );
}


// ═════════════════════════════════════════════════════════════════════════
// PatientBaselineBlock — captures the patient's baseline (past + ongoing
// conditions, past surgeries). Sealed at first source of truth: once
// stored for a (doctor, patient) pair, subsequent visits render read-only.
// ═════════════════════════════════════════════════════════════════════════
function PatientBaselineBlock({ target }) {
  const phoneHash = target?.patient_phone_hash || '';
  const [row, setRow] = useState(null);
  const [conds, setConds] = useState([]);
  const [surgs, setSurgs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState('');

  useEffect(() => {
    if (!phoneHash) return;
    let live = true;
    api.get(`/dashboard/patient-baseline/${phoneHash}`)
      .then(r => {
        if (!live) return;
        setRow(r.data || null);
        setConds((r.data && r.data.baseline_conditions) || []);
        setSurgs((r.data && r.data.past_surgeries) || []);
      })
      .catch(() => {});
    return () => { live = false; };
  }, [phoneHash]);

  const sealed = !!(row && row.sealed);
  const canEdit = !!phoneHash && !sealed;

  const addCond = () => setConds([...conds, { id: `bc-${Date.now()}`, name: '', started_when: '', status: 'ongoing', current_meds: '', notes: '' }]);
  const rmCond = (i) => setConds(conds.filter((_, x) => x !== i));
  const updCond = (i, patch) => setConds(conds.map((x, j) => j === i ? { ...x, ...patch } : x));
  const addSurg = () => setSurgs([...surgs, { id: `ps-${Date.now()}`, name: '', done_when: '', for_condition: '', status: 'resolved', notes: '' }]);
  const rmSurg = (i) => setSurgs(surgs.filter((_, x) => x !== i));
  const updSurg = (i, patch) => setSurgs(surgs.map((x, j) => j === i ? { ...x, ...patch } : x));

  const save = async () => {
    if (!canEdit) return;
    setSaving(true);
    try {
      const clean_c = conds.filter(c => (c.name || '').trim());
      const clean_s = surgs.filter(s => (s.name || '').trim());
      const { data } = await api.post(`/dashboard/patient-baseline/${phoneHash}`, { baseline_conditions: clean_c, past_surgeries: clean_s });
      setSavedAt(data.captured_at || new Date().toISOString());
      setRow({ ...(row || {}), sealed: true, baseline_conditions: clean_c, past_surgeries: clean_s, captured_at: data.captured_at });
    } finally { setSaving(false); }
  };

  if (!phoneHash) return null;

  return (
    <div className="mb-3 rounded-2xl border border-amber-200 bg-amber-50/40 p-3" data-testid="dhf-baseline-block">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <p className="text-[10px] font-bold uppercase tracking-wider text-amber-800">Patient Baseline · past + ongoing conditions &amp; surgeries</p>
        <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full border ${sealed ? 'bg-slate-100 text-slate-600 border-slate-300' : 'bg-emerald-100 text-emerald-800 border-emerald-300'}`}>
          {sealed ? 'Sealed · first source of truth' : 'First visit · capture now'}
        </span>
      </div>
      <div className="mb-3">
        <div className="flex items-center justify-between mb-1">
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-700">1 · Baseline Health Conditions</p>
          {canEdit && <button type="button" onClick={addCond} className="text-[10px] font-semibold text-amber-800 hover:text-amber-900" data-testid="dhf-baseline-cond-add">＋ Add</button>}
        </div>
        {(!conds.length && sealed) && <p className="text-[10px] text-slate-500 italic">No baseline conditions captured.</p>}
        <ul className="space-y-1.5">
          {conds.map((c, i) => (
            <li key={c.id || i} className="grid grid-cols-1 md:grid-cols-[1.5fr_0.8fr_0.9fr_1.5fr_auto] gap-1.5 md:items-center" data-testid={`dhf-baseline-cond-row-${i}`}>
              <input value={c.name || ''} readOnly={!canEdit} onChange={e => updCond(i, { name: e.target.value })} placeholder="Condition name" className={`rounded-md border px-2 py-1 text-[11px] ${canEdit ? 'border-slate-200 bg-white' : 'border-slate-100 bg-slate-50 text-slate-600'}`} data-testid={`dhf-baseline-cond-name-${i}`} />
              <input value={c.started_when || ''} readOnly={!canEdit} onChange={e => updCond(i, { started_when: e.target.value })} placeholder="Started (year)" className={`rounded-md border px-2 py-1 text-[11px] ${canEdit ? 'border-slate-200 bg-white' : 'border-slate-100 bg-slate-50 text-slate-600'}`} />
              <select value={c.status || 'ongoing'} disabled={!canEdit} onChange={e => updCond(i, { status: e.target.value })} className={`rounded-md border px-2 py-1 text-[11px] ${canEdit ? 'border-slate-200 bg-white' : 'border-slate-100 bg-slate-50 text-slate-600'}`}>
                <option value="ongoing">Ongoing</option>
                <option value="resolved">Resolved</option>
              </select>
              <input value={c.current_meds || ''} readOnly={!canEdit} onChange={e => updCond(i, { current_meds: e.target.value })} placeholder={c.status === 'ongoing' ? 'Current medicines' : 'Notes'} className={`rounded-md border px-2 py-1 text-[11px] ${canEdit ? 'border-slate-200 bg-white' : 'border-slate-100 bg-slate-50 text-slate-600'}`} />
              {canEdit && <button type="button" onClick={() => rmCond(i)} className="text-rose-500 text-xs justify-self-end" title="Remove">×</button>}
            </li>
          ))}
        </ul>
      </div>
      <div>
        <div className="flex items-center justify-between mb-1">
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-700">2 · Past Surgeries</p>
          {canEdit && <button type="button" onClick={addSurg} className="text-[10px] font-semibold text-amber-800 hover:text-amber-900" data-testid="dhf-baseline-surg-add">＋ Add</button>}
        </div>
        {(!surgs.length && sealed) && <p className="text-[10px] text-slate-500 italic">No past surgeries captured.</p>}
        <ul className="space-y-1.5">
          {surgs.map((s, i) => (
            <li key={s.id || i} className="grid grid-cols-1 md:grid-cols-[1.4fr_0.8fr_1.2fr_0.9fr_auto] gap-1.5 md:items-center" data-testid={`dhf-baseline-surg-row-${i}`}>
              <input value={s.name || ''} readOnly={!canEdit} onChange={e => updSurg(i, { name: e.target.value })} placeholder="Surgery name" className={`rounded-md border px-2 py-1 text-[11px] ${canEdit ? 'border-slate-200 bg-white' : 'border-slate-100 bg-slate-50 text-slate-600'}`} data-testid={`dhf-baseline-surg-name-${i}`} />
              <input value={s.done_when || ''} readOnly={!canEdit} onChange={e => updSurg(i, { done_when: e.target.value })} placeholder="Done (year)" className={`rounded-md border px-2 py-1 text-[11px] ${canEdit ? 'border-slate-200 bg-white' : 'border-slate-100 bg-slate-50 text-slate-600'}`} />
              <input value={s.for_condition || ''} readOnly={!canEdit} onChange={e => updSurg(i, { for_condition: e.target.value })} placeholder="For which condition" className={`rounded-md border px-2 py-1 text-[11px] ${canEdit ? 'border-slate-200 bg-white' : 'border-slate-100 bg-slate-50 text-slate-600'}`} />
              <select value={s.status || 'resolved'} disabled={!canEdit} onChange={e => updSurg(i, { status: e.target.value })} className={`rounded-md border px-2 py-1 text-[11px] ${canEdit ? 'border-slate-200 bg-white' : 'border-slate-100 bg-slate-50 text-slate-600'}`}>
                <option value="resolved">Resolved</option>
                <option value="continues">Continues</option>
              </select>
              {canEdit && <button type="button" onClick={() => rmSurg(i)} className="text-rose-500 text-xs justify-self-end" title="Remove">×</button>}
            </li>
          ))}
        </ul>
      </div>
      {canEdit && (conds.length > 0 || surgs.length > 0) && (
        <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
          <p className="text-[10px] text-slate-500 italic">This gets sealed on save — subsequent visits will show these read-only.</p>
          <button type="button" onClick={save} disabled={saving} className="px-3 py-1.5 rounded-lg bg-amber-600 text-white text-xs font-semibold disabled:opacity-40 hover:bg-amber-700" data-testid="dhf-baseline-save">
            {saving ? 'Sealing…' : 'Seal baseline (first source of truth)'}
          </button>
        </div>
      )}
      {sealed && row?.captured_at && (
        <p className="text-[10px] text-slate-500 mt-2">Captured on {new Date(row.captured_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}. Read-only on all subsequent visits.</p>
      )}
      {savedAt && !row?.captured_at && (
        <p className="text-[10px] text-emerald-700 mt-2">Baseline sealed just now.</p>
      )}
    </div>
  );
}
